// ---------------------------------------------------------------------------
// The game engine.
//
// Two invariants hold this file together, and both are load bearing.
//
// PURITY. `applyAction` never reads `Date.now()`, never calls `Math.random()`,
// and never mutates its input. Every action carries `now`. Everything random
// (letter piles, number tiles, targets, the conundrum) is drawn by the server
// and arrives on the action. That is what makes the engine testable, and what
// makes restart survival correct: replaying a snapshot plus an action log lands
// in exactly the same state.
//
// SANITIZATION. `toClientState` is the only door between hidden state and a
// client. A bug there shows everyone's word to everyone, which is the worst
// failure this game has. Most of the defence is structural rather than
// filtered: `ClientRound` has no `vowelPile`, no `solution`, and no
// `spectatorSubmissions`, so those cannot be forgotten on the way past. Drafts
// never enter `ServerGameState` at all.
//
// A third, quieter idea: `round.clock` is always the clock for the *current
// phase* of that round. It holds the round-intro hold, then the selection
// budget, then the play clock, then the reveal pacing, then the reveal's
// auto-advance. One field means one code path for pausing, for resuming across a
// restart, and for `nextTickAt`, and nothing can be left running by accident.
// ---------------------------------------------------------------------------
import { APPLIED_ACTION_ID_LIMIT, AUDIENCE_SEAT_ID, CHAT_HISTORY_LIMIT, CONUNDRUM_POINTS, DEFAULT_SETTINGS, HOST_TRANSFER_AFTER_MS, LETTERS_TILE_COUNT, LOG_HISTORY_LIMIT, NUMBERS_DEAL_MS, NUMBERS_LARGE_TILES, NUMBERS_MAX_LARGE, NUMBERS_MIN_LARGE, NUMBERS_SMALL_TILES, NUMBERS_TARGET_HOLD_MS, NUMBERS_TARGET_MAX, NUMBERS_TARGET_MIN, NUMBERS_TARGET_SPIN_MS, NUMBERS_TILE_COUNT, PLAYER_COLORS, RESUME_COUNTDOWN_MS, REVEAL_AUTO_ADVANCE_MS, ROUND_INTRO_MS, ROUND_SEQUENCE_MAX, SAFETY_SETTINGS, TEAM_COLORS, colorForSeat, gameCountsForStats, reasoningAllowed, revealHoldMs, revealIsAggregated, revealLeadMs, } from './constants.js';
import { acceptsLateLock, effectiveDeadline, elapsedMs, isExpired, pauseClock, resumeClock, startClock, } from './clock.js';
import { allowedPicks, autoFillLetters, canPick, drawLetter } from './letters.js';
import { foldNumbersBest, validateStepList } from './numbers.js';
import { audienceSubmission, buildRoundAggregate, conundrumBuzzerOff, audienceSeatAppears, cumulativeTeamScores, determineWinners, resolveWinners, revealOrder, scoreConundrum, scoreLettersWord, scoreNumbersValue, scoreRound, seatScoresThisGame, } from './scoring.js';
import { canBuildFromTiles, normalizeWord, sanitizeSettings, uniqueDisplayName, validateChatBody, validateDisplayName, validateSettings, } from './validation.js';
const PLAY_PHASES = ['letters-play', 'numbers-play', 'conundrum-play'];
const REVEAL_PHASES = [
    'letters-reveal',
    'numbers-reveal',
    'conundrum-reveal',
];
const SELECTION_PHASES = ['letters-selection', 'numbers-selection'];
/**
 * Whether a clock is running against this player's answer right now.
 *
 * Exported because the disconnect overlay needs it and must not re-derive it.
 * That overlay says "the round is still running, your answer is saved", which is
 * the load-bearing half of DESIGN §8's two-kinds-of-pause rule — and it was
 * saying it to somebody who dropped in the lobby, where there is no round, and
 * between rounds, where there is no answer. A sentence that is false in two of
 * the places it appears stops being read in the third.
 *
 * Selection is deliberately in: the chooser is on a clock and the room is
 * watching it. `round-intro` and the reveal are deliberately out — a clock is
 * running in both, but it is the show's clock rather than the player's, and
 * nothing anybody types is riding on it.
 */
export function isLiveRoundPhase(phase) {
    return PLAY_PHASES.includes(phase) || SELECTION_PHASES.includes(phase);
}
// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------
function fail(error, code) {
    return code === undefined ? { ok: false, error } : { ok: false, error, code };
}
/**
 * Fill in anything a snapshot from an older build came back without.
 *
 * Every field here is one that was added after rooms were already being written
 * through to SQLite, so a restored room can arrive holding `undefined` where the
 * type promises a value. Restart survival is the whole point of the snapshot, so
 * this is cheaper than making every reader defensive.
 */
function asEngine(state) {
    const readyIds = Array.isArray(state.readyIds) ? state.readyIds : [];
    const teams = Array.isArray(state.teams) ? state.teams : [];
    // Zero rather than `chat.length`: the count only has to be monotonic, and
    // seeding it from an array that is already capped and can already have been
    // trimmed would start it out disagreeing with itself. A restored room marks
    // its own history read once and then behaves. See `ServerGameState.chatSeq`.
    const chatSeq = typeof state.chatSeq === 'number' ? state.chatSeq : 0;
    return {
        ...state,
        readyIds,
        teams,
        chatSeq,
        rounds: (state.rounds ?? []).map(withRevealFields),
    };
}
/** A round from an older snapshot, given the reveal-pacing fields it predates. */
function withRevealFields(round) {
    if (typeof round.revealCursor === 'number' &&
        round.nextRevealAt !== undefined &&
        round.revealAdvanceAt !== undefined) {
        return round;
    }
    return patchRound(round, {
        revealCursor: round.revealCursor ?? 0,
        nextRevealAt: round.nextRevealAt ?? null,
        revealAdvanceAt: round.revealAdvanceAt ?? null,
    });
}
function draftOf(state) {
    return { state: asEngine(state), events: [] };
}
function done(draft) {
    return { ok: true, state: draft.state, events: draft.events };
}
/**
 * Deterministic PRNG, so `applyAction` can make an "at random" choice without
 * touching `Math.random`. The letter piles arrive pre-shuffled by the server,
 * so the randomness that matters already happened; this only breaks ties
 * between two legal kinds during an auto-fill.
 */
function seededRandom(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
function capTail(items, limit) {
    return items.length <= limit ? items.slice() : items.slice(items.length - limit);
}
// --- Clock predicates ------------------------------------------------------
/**
 * A clock deliberately stopped at or past its own deadline: the round's time is
 * up and frozen there. Distinct from a server pause, which stops a clock with
 * time still on it.
 *
 * This is the one state-only signal that a play clock expired, which matters
 * because `toClientState` has no `now` and still has to know whether declared
 * lengths are public yet.
 */
function isStoppedAtDeadline(clock) {
    return clock.pausedAt !== null && effectiveDeadline(clock) <= clock.pausedAt;
}
/** Freeze an expired clock exactly at its deadline. Idempotent, never drifts. */
function freezeAtDeadline(clock, now) {
    if (clock.pausedAt !== null)
        return clock;
    const deadline = effectiveDeadline(clock);
    return now < deadline ? clock : { ...clock, pausedAt: deadline };
}
/**
 * Whether an answer that arrived at `at` still counts for this player.
 *
 * The heartbeat clamp exists to prove a human's browser was still there, which
 * is what closes the pull-the-cable exploit. A bot has no browser and no socket,
 * so its `lastHeartbeat` is frozen at the moment it was added to the lobby and
 * every bot answer in every game that started more than HEARTBEAT_GRACE_MS after
 * that would be rejected as late. Bots are held to the round deadline only.
 */
function acceptsAnswerAt(player, clock, at) {
    if (player.isBot)
        return at <= effectiveDeadline(clock);
    return acceptsLateLock(clock, player.lastHeartbeat, at);
}
/**
 * Patch the fields every round shares. Written as an exhaustive switch rather
 * than a spread over the union so the discriminant survives and nothing has to
 * be cast.
 */
function patchRound(round, patch) {
    switch (round.type) {
        case 'letters':
            return { ...round, ...patch };
        case 'numbers':
            return { ...round, ...patch };
        case 'conundrum':
            return { ...round, ...patch };
    }
}
function mapRoundClocks(round, fn) {
    const clock = round.clock === null ? null : fn(round.clock);
    if (round.type === 'conundrum') {
        return {
            ...round,
            clock,
            buzzClock: round.buzzClock === null ? null : fn(round.buzzClock),
        };
    }
    return patchRound(round, { clock });
}
function replaceRound(state, round) {
    const rounds = state.rounds.map((existing) => (existing.index === round.index ? round : existing));
    return { ...state, rounds };
}
function findRound(state, index) {
    return state.rounds.find((round) => round.index === index) ?? null;
}
// --- Players ---------------------------------------------------------------
function patchPlayer(state, playerId, patch) {
    const player = state.players[playerId];
    if (player === undefined)
        return state;
    return { ...state, players: { ...state.players, [playerId]: { ...player, ...patch } } };
}
function seatedIds(state) {
    return state.playerOrder.filter((id) => state.players[id]?.role === 'player');
}
/**
 * Whether this room is playing in teams.
 *
 * **Derived, never stored.** There was a `teamsEnabled` setting and it was a
 * second copy of a fact the roster already held, which meant it could disagree
 * with the roster — and did, in both directions: `createTeam` was its only
 * writer, so a host who opened a team and closed it left the room flagged as a
 * teams game for the rest of its life, and the browse list labelled a room with
 * no teams in it "teams". The flag's own switch then had to clear the teams, so
 * turning it off had to reach into every player's `teamId`, which is the fix for
 * a problem that only existed because of the flag.
 *
 * **A team that EXISTS but is EMPTY still counts**, and that is the correct
 * reading rather than a rounding of one. A host who opens four teams and then
 * seats people into them has said what the room is playing before anybody is in
 * one, and `assignTeam` refuses while teams are off — so a room that could not
 * count an empty team could never get its first member into one. Nothing else
 * needs a special case for it: an empty team contributes nothing to best-of
 * scoring and `cumulativeTeamScores` gives it no bucket, so it is a container
 * with nobody in it and no row anywhere.
 */
export function teamsAreOn(state) {
    return (state.teams?.length ?? 0) > 0;
}
/**
 * Whether the room has run out of seats.
 *
 * **`maxPlayers: null` is no limit and is the default**, so this is false in the
 * ordinary room. The cap survives as something a host can set on purpose; what
 * a room that wants to stop letting people in reaches for is `roomLocked`, which
 * seats nobody new rather than turning them away. See `GameSettings.maxPlayers`.
 */
function roomIsFull(state) {
    const cap = state.settings.maxPlayers;
    if (cap === null || cap === undefined)
        return false;
    return seatedIds(state).length >= cap;
}
/**
 * Whether somebody who is not the host may open a team, close an empty one, or
 * change their own colour. See `GameSettings.playerPermissions`.
 *
 * The host is always allowed, so this reads as "may this person do it" rather
 * than as "is the setting on" — which is what keeps every call site from having
 * to remember the `|| isHost` half.
 */
function mayUsePlayerPowers(state, playerId) {
    return state.settings.playerPermissions === true || isHost(state, playerId);
}
function publicPlayerOf(state, player) {
    return {
        id: player.id,
        username: player.username,
        registered: player.userId !== null,
        displayName: player.displayName,
        color: player.color,
        isBot: player.isBot,
        botKind: player.botKind,
        botDifficulty: player.botDifficulty,
        botModel: player.botModel,
        botReasoning: player.botReasoning,
        role: player.role,
        leftSeat: (player.leftSeatAt ?? null) !== null,
        teamId: player.teamId,
        score: player.score,
        connected: player.connected,
        muted: player.muted,
        waitingForNextGame: player.waitingForNextGame,
        isHost: state.hostId === player.id,
    };
}
// --- Chat and log ----------------------------------------------------------
function pushLog(draft, kind, message, now) {
    const entry = {
        id: `log-${now}-${draft.state.log.length}`,
        kind,
        message,
        at: now,
    };
    draft.state = { ...draft.state, log: capTail([...draft.state.log, entry], LOG_HISTORY_LIMIT) };
    draft.events.push({ type: 'log-entry', entry });
}
function pushChat(draft, message) {
    draft.state = {
        ...draft.state,
        chat: capTail([...draft.state.chat, message], CHAT_HISTORY_LIMIT),
        // The only writer, and it only ever adds. Nothing that removes a line
        // touches it — that is the whole of what makes an unread count safe against
        // a host taking one down. See `ServerGameState.chatSeq`.
        chatSeq: (draft.state.chatSeq ?? 0) + 1,
    };
    draft.events.push({ type: 'chat-message', message });
}
/**
 * Whether one player may read one chat line.
 *
 * **One implementation, read from both ends.** `toClientState` uses it to decide
 * what goes on the wire, and `deleteChatMessage` uses it to decide what the host
 * is allowed to name. Those two have to agree exactly or the delete becomes an
 * oracle: a host who is refused for "no such message" but succeeds on a team
 * line they were never shown has learned that the line exists, and repeating
 * that with guessed ids is a way of mapping a conversation they are not in. The
 * ids are `randomUUID`s so nobody is guessing one in practice — this is the same
 * reasoning the definition endpoint's single 403 is built on, where "not in that
 * round", "not revealed yet" and "not a word" are deliberately one answer.
 *
 * A system line belongs to everyone. The admin spy view is not modelled here:
 * it reads every line and is handled by its own branch at the call site, because
 * it is a delivery decision rather than a rule about who is in the conversation.
 */
export function canSeeChatMessage(message, viewer) {
    if (message.system || message.channel !== 'team')
        return true;
    if (viewer === undefined)
        return false;
    return message.teamId !== null && message.teamId === viewer.teamId;
}
function setPhase(draft, phase) {
    draft.state = { ...draft.state, phase };
    draft.events.push({ type: 'phase-changed', phase, roundIndex: draft.state.currentRoundIndex });
}
// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------
export function createInitialState(roomCode, hostId, now, settings) {
    const merged = { ...DEFAULT_SETTINGS, ...(settings ?? {}) };
    const state = {
        roomCode,
        gameId: null,
        phase: 'lobby',
        settings: { ...merged, roundSequence: [...merged.roundSequence] },
        players: {},
        playerOrder: [],
        // No teams until somebody makes one. A room that hands out four empty teams
        // nobody asked for has to hide them again in every client, and hiding them
        // client-side is exactly how an empty team ended up visible only to whoever
        // created it.
        teams: [],
        hostId,
        chooserRotationIndex: 0,
        currentRoundIndex: 0,
        rounds: [],
        serverPaused: false,
        serverPauseReason: null,
        resumingAt: null,
        createdAt: now,
        startedAt: null,
        endedAt: null,
        chat: [],
        log: [],
        appliedActionIds: [],
        winnerIds: [],
        readyIds: [],
        chatSeq: 0,
    };
    return state;
}
export function currentRound(state) {
    return findRound(state, state.currentRoundIndex);
}
export function isHost(state, playerId) {
    return state.hostId === playerId;
}
/**
 * Chooser rotation: one turn per team, cycling across groups before cycling
 * within them. Red, blue, yellow gives red 1, blue 1, yellow 1, red 2, blue 2.
 * Solo players are one-member teams, so a free-for-all room rotates in seat
 * order and this needs no special case.
 */
export function nextChooserId(state) {
    // `seatedIds` reads `playerOrder`, which the audience seat is deliberately not
    // in — so it is never asked to call a board and needs no clause here saying
    // so. That is the whole reason it lives as a reserved id in `submissions`
    // rather than as a `ServerPlayer`. See `AUDIENCE_SEAT_ID`.
    const seated = seatedIds(state);
    if (seated.length === 0)
        return null;
    const teams = teamsAreOn(state);
    const groupKeys = [];
    const groups = new Map();
    for (const id of seated) {
        const player = state.players[id];
        const key = teams ? (player.teamId ?? id) : id;
        const bucket = groups.get(key);
        if (bucket === undefined) {
            groupKeys.push(key);
            groups.set(key, [id]);
        }
        else {
            bucket.push(id);
        }
    }
    const index = Math.max(0, Math.trunc(state.chooserRotationIndex));
    const group = groups.get(groupKeys[index % groupKeys.length]);
    if (group === undefined || group.length === 0)
        return null;
    const cycle = Math.floor(index / groupKeys.length);
    return group[cycle % group.length];
}
// ---------------------------------------------------------------------------
// applyAction
// ---------------------------------------------------------------------------
function actionIdOf(action) {
    return 'actionId' in action && typeof action.actionId === 'string' ? action.actionId : null;
}
function withAppliedActionId(state, actionId) {
    const ids = capTail([...state.appliedActionIds, actionId], APPLIED_ACTION_ID_LIMIT);
    return { ...state, appliedActionIds: ids };
}
/** Pure. Never reads Date.now(), never mutates its input. */
export function applyAction(state, action) {
    const actionId = actionIdOf(action);
    // A replay is success with no effect, not an error. The client is legitimately
    // retrying after a reconnect and must not be told it did something wrong.
    if (actionId !== null && state.appliedActionIds.includes(actionId)) {
        return { ok: true, state, events: [] };
    }
    const result = dispatch(state, action);
    if (result.ok && actionId !== null) {
        return { ...result, state: withAppliedActionId(result.state, actionId) };
    }
    return result;
}
function dispatch(state, action) {
    switch (action.kind) {
        case 'add-player':
            return addPlayer(state, action.now, action.player);
        case 'remove-player':
            return removePlayer(state, action.now, action.playerId);
        case 'set-connected':
            return setConnected(state, action.now, action.playerId, action.connected);
        case 'heartbeat':
            return heartbeat(state, action.now, action.playerId);
        case 'rename':
            return rename(state, action.now, action.playerId, action.displayName);
        case 'identify':
            return identify(state, action.playerId, action.userId, action.username);
        case 'set-color':
            return setColor(state, action.playerId, action.color);
        case 'set-role':
            return setRole(state, action.playerId, action.role);
        case 'assign-role':
            return assignRole(state, action.playerId, action.targetId, action.role);
        case 'leave-seat':
            return leaveSeat(state, action.now, action.playerId);
        case 'transfer-host':
            return transferHost(state, action.now, action.playerId, action.automatic);
        case 'set-muted':
            return setMuted(state, action.now, action.playerId, action.muted);
        case 'update-settings':
            return updateSettings(state, action.now, action.playerId, action.settings);
        case 'set-ready':
            return setReady(state, action.playerId, action.ready);
        case 'assign-team':
            return assignTeam(state, action.now, action.playerId, action.teamId, action.actorId ?? action.playerId);
        case 'create-team':
            return createTeam(state, action.now, action.playerId);
        case 'delete-team':
            return deleteTeam(state, action.now, action.playerId, action.teamId);
        case 'add-bot':
            return addBot(state, action.now, action.botId, action.name, action.color, action.difficulty, action.botKind ?? 'heuristic', action.model ?? null, action.reasoning ?? false, action.ownerUserId ?? null);
        case 'remove-bot':
            return removeBot(state, action.now, action.playerId);
        case 'set-bot-difficulty':
            return setBotDifficulty(state, action.playerId, action.difficulty);
        case 'set-bot-model':
            return setBotModel(state, action.playerId, action.model, action.reasoning);
        case 'set-bot-kind':
            return setBotKind(state, action.playerId, action.botKind, action.difficulty, action.model ?? null, action.reasoning ?? false, action.ownerUserId ?? null);
        case 'extend-room':
            return extendRoom(state, action.now, action.playerId);
        case 'start-game':
            return startGame(state, action.now, action.gameId);
        case 'pick-letter':
            return pickLetter(state, action.now, action.playerId, action.letterKind);
        case 'auto-fill-letters':
            return autoFill(state, action.now);
        case 'choose-large-count':
            return chooseLargeCount(state, action.now, action.playerId, action.largeCount);
        case 'reveal-number-tile':
            return revealNumberTile(state, action.now, action.tile);
        case 'reveal-number-target':
            return revealNumberTarget(state, action.now, action.target);
        case 'begin-numbers-play':
            return beginNumbersPlay(state, action.now);
        case 'lock-letters':
            return lockLetters(state, action.playerId, action.word, action.clientLockedAt, action.reasoning ?? null, action.allowUnbuildable ?? false);
        case 'submit-word':
            return submitWord(state, action.playerId, action.word, action.valid, action.score, action.clientSubmittedAt);
        case 'lock-numbers':
            return lockNumbers(state, action.playerId, action.steps, action.clientLockedAt, action.reasoning ?? null);
        case 'buzz':
            return buzz(state, action.now, action.playerId, action.compensatedAt);
        case 'submit-conundrum':
            return submitConundrum(state, action.now, action.playerId, action.answer, action.correct, action.clientLockedAt, action.reasoning ?? null);
        case 'unlock':
            return unlock(state, action.playerId);
        case 'adopt-draft':
            return adoptDraft(state, action.now, action.playerId, action.draft);
        case 'begin-round':
            return beginRound(state, action.now, action.setup);
        case 'judge-round':
            return judgeRound(state, action.now, action.judgments, action.spectatorJudgments, action.best);
        case 'override-word':
            return overrideWord(state, action.now, action.playerId, action.targetId, action.ruling);
        case 'advance':
            return advance(state, action.now);
        case 'advance-reveal':
            return advanceReveal(state, action.now, action.playerId);
        case 'record-shadow-answer':
            return recordShadowAnswer(state, action.roundIndex, action.shadow);
        case 'set-reveal-paused':
            return setRevealPaused(state, action.now, action.playerId, action.paused);
        case 'force-skip-round':
            return forceSkipRound(state, action.now, action.playerId);
        case 'end-game':
            return endGame(state, action.now, action.playerId);
        case 'return-to-lobby':
            return returnToLobby(state, action.now, action.playerId);
        case 'tick':
            return tick(state, action.now);
        case 'server-pause':
            return serverPause(state, action.now, action.reason);
        case 'server-resume':
            return serverResume(state, action.now);
        case 'begin-resume-countdown':
            return beginResumeCountdown(state, action.now);
        case 'chat':
            return chat(state, action.now, action);
        case 'delete-chat-message':
            return deleteChatMessage(state, action.now, action.playerId, action.messageId);
        case 'purge-chat':
            return purgeChat(state, action.now, action.playerId, action.authorId);
        case 'system-message':
            return systemMessage(state, action.now, action.body);
    }
}
// ---------------------------------------------------------------------------
// Membership
// ---------------------------------------------------------------------------
function addPlayer(state, now, input) {
    if (state.players[input.id] !== undefined) {
        return fail('that player is already in the room', 'invalid-action');
    }
    const draft = draftOf(state);
    const inLobby = state.phase === 'lobby';
    const wantsSeat = input.role === 'player';
    const spectators = Object.values(state.players).filter((p) => p.role === 'spectator').length;
    const door = state.settings.roomLock ?? 'open';
    const benchIsOpen = door !== 'room';
    // A late join becomes a spectator and waits for the next game rather than
    // being refused. The seat arrives at back-to-lobby.
    //
    // **A locked room does the same thing and does NOT set the flag.** A lock is
    // not a wait: nothing about back-to-lobby unlocks the room, so promising a
    // seat at the next game would be a promise the room has said it is not making.
    // See `GameSettings.roomLock`.
    //
    // **A returning player is not a new arrival and the lock does not touch
    // them.** Somebody whose account or device already holds a seat here is coming
    // back to it, and a streamer whose laptop slept mid-lobby must not find
    // themselves on the bench of their own room. The seat takeover itself is the
    // server's — it matches the seat and never dispatches this at all — and this
    // is the same rule written where it can be tested without a socket.
    let role = input.role;
    let waitingForNextGame = false;
    if (wantsSeat && !inLobby) {
        role = 'spectator';
        waitingForNextGame = true;
    }
    else if (wantsSeat &&
        door !== 'open' &&
        // The host is never locked out of their own room. `createInitialState` names
        // a host before anybody has joined, so a room created with the lock already
        // on would otherwise bench the person who set it — and leave a locked room
        // with a host on the bench and no way to seat anybody, since `assign-role`
        // is the only door back and it is theirs.
        input.id !== state.hostId &&
        returningSeat(state, input) === null) {
        role = 'spectator';
    }
    else if (wantsSeat && roomIsFull(state)) {
        return fail('the room is full', 'room-full');
    }
    if (role === 'spectator' && !benchIsOpen) {
        // A closed room is refusing everybody, which is a legal thing to have set up
        // and a baffling thing to be told "the room is full" about — nothing is
        // full, the door is simply shut. One sentence now rather than two: with the
        // door as a single control there is no longer a half of it to be honest
        // about separately, and "you cannot play, and you cannot watch either" was
        // describing a two-boolean shape that no longer exists.
        return fail('this room is closed', 'room-full');
    }
    if (role === 'spectator' &&
        state.settings.maxSpectators !== null &&
        state.settings.maxSpectators !== undefined &&
        spectators >= state.settings.maxSpectators) {
        return fail('the room has no room left to watch', 'room-full');
    }
    // Two people can ask for the same colour: the picker is on the join screen,
    // before either of them is in the room to see the other. Whoever arrives
    // second gets the nearest free palette entry rather than an identical chip.
    //
    // Null when the room is about to be bigger than the palette — `colorForSeat`
    // is the one place that is decided, and `withResolvedColors` below settles it
    // for everybody rather than only for the arrival.
    // Nothing at all for somebody arriving to watch: see `withResolvedColors`,
    // which is where that rule is argued. They are given one the moment they take
    // a place in the game, by the same function.
    const wanted = input.color;
    const color = role === 'spectator'
        ? null
        : wanted.length > 0 && !colorIsTeamOwned(wanted) && !colorTakenBy(state, wanted, input.id)
            ? wanted
            : freeColor(state, input.id);
    // Two people called Isaac is a collision, not a refusal. The one already in
    // the room keeps the name and the arrival becomes `Isaac (2)`, on join and on
    // rename alike — see `uniqueDisplayName`. Applied here rather than in
    // `socket.ts` so that a bot, an admin force-rename and a restored room all go
    // through the same rule, and so the engine's own state can never hold two
    // players the running order cannot tell apart.
    const displayName = uniqueDisplayName(input.displayName, Object.values(state.players).map((other) => other.displayName));
    const player = {
        id: input.id,
        userId: input.userId,
        username: input.username,
        displayName,
        deviceId: input.deviceId,
        color,
        isBot: input.isBot === true,
        botKind: input.isBot === true ? (input.botKind ?? 'heuristic') : null,
        botDifficulty: input.botDifficulty ?? null,
        botModel: input.botModel ?? null,
        botReasoning: input.botReasoning ?? false,
        // Only ever set through `add-bot`; a joining human has nobody paying for it.
        botOwnerUserId: null,
        role,
        leftSeatAt: null,
        teamId: null,
        score: 0,
        connected: true,
        lastHeartbeat: now,
        joinedAt: now,
        disconnectedAt: null,
        muted: false,
        waitingForNextGame,
        reconnectToken: input.reconnectToken,
        ip: input.ip,
    };
    draft.state = {
        ...draft.state,
        players: { ...draft.state.players, [player.id]: player },
        playerOrder: role === 'player' ? [...draft.state.playerOrder, player.id] : draft.state.playerOrder,
    };
    draft.state = withResolvedColors(draft.state);
    draft.events.push({ type: 'player-joined', player: publicPlayerOf(draft.state, draft.state.players[player.id]) });
    pushLog(draft, 'player-joined', `${player.displayName} joined`, now);
    return done(draft);
}
/**
 * A seat this arrival already holds, matched by account first and device second.
 *
 * It is the rule CLAUDE.md states as "one account may hold one seat in a room",
 * asked here for one purpose only: deciding whether `roomLocked` is looking at
 * somebody new. It does NOT take the seat over — that is the server's, which
 * matches the seat before `add-player` is ever dispatched — so a room that
 * reaches here with a match is one where the two paths disagreed, and the safe
 * answer is not to demote the person who was already playing.
 *
 * Bots are excluded on both sides: one lobby seating the same model twice is
 * deliberate, and a bot has neither an account nor a device.
 */
function returningSeat(state, input) {
    if (input.isBot === true)
        return null;
    const seats = Object.values(state.players).filter((player) => !player.isBot && player.role === 'player');
    if (input.userId !== null) {
        const byAccount = seats.find((player) => player.userId === input.userId);
        if (byAccount !== undefined)
            return byAccount;
    }
    if (input.deviceId !== null) {
        const byDevice = seats.find((player) => player.deviceId === input.deviceId);
        if (byDevice !== undefined)
            return byDevice;
    }
    return null;
}
function removePlayer(state, now, playerId) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    const draft = draftOf(state);
    const players = { ...draft.state.players };
    delete players[playerId];
    draft.state = {
        ...draft.state,
        players,
        playerOrder: draft.state.playerOrder.filter((id) => id !== playerId),
        readyIds: draft.state.readyIds.filter((id) => id !== playerId),
    };
    // Drop their answer from a round still in flight, so the reveal never names
    // somebody the client has no record of. Judged rounds are history and stay.
    const round = currentRound(draft.state);
    if (round !== null && round.revealedAt === null) {
        const submissions = { ...round.submissions };
        const spectatorSubmissions = { ...round.spectatorSubmissions };
        delete submissions[playerId];
        delete spectatorSubmissions[playerId];
        draft.state = replaceRound(draft.state, patchRound(round, { submissions, spectatorSubmissions }));
    }
    // A departure can put the room back under the palette line, and colour comes
    // back to everybody when it does. See `withResolvedColors`.
    draft.state = withResolvedColors(draft.state);
    draft.events.push({ type: 'player-left', playerId, displayName: player.displayName });
    pushLog(draft, 'player-left', `${player.displayName} left`, now);
    if (draft.state.hostId === playerId)
        handoffHost(draft, now, true);
    return done(draft);
}
function setConnected(state, now, playerId, connected) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    const draft = draftOf(state);
    draft.state = patchPlayer(draft.state, playerId, {
        connected,
        disconnectedAt: connected ? null : now,
        lastHeartbeat: connected ? now : player.lastHeartbeat,
    });
    draft.events.push(connected
        ? { type: 'player-reconnected', playerId }
        : { type: 'player-disconnected', playerId });
    // A single player's disconnect freezes NOBODY'S clock, including their own.
    // Freezing one clock while the others run is free thinking time. The only
    // exception is the host's explicit, trust-based `pauseOnDisconnect`, which is
    // off by default and labelled exploitable in the interface.
    if (draft.state.settings.pauseOnDisconnect && !draft.state.serverPaused) {
        const anyDown = Object.values(draft.state.players).some((candidate) => !candidate.isBot && !candidate.connected);
        draft.state = {
            ...draft.state,
            rounds: draft.state.rounds.map((round) => mapRoundClocks(round, (clock) => isStoppedAtDeadline(clock)
                ? clock
                : anyDown
                    ? pauseClock(clock, now)
                    : resumeClock(clock, now))),
        };
        draft.state = syncRevealDeadlines(draft.state);
    }
    return done(draft);
}
function heartbeat(state, now, playerId) {
    if (state.players[playerId] === undefined)
        return fail('no such player', 'invalid-action');
    // Deliberately eventless: a heartbeat a second per player must not fan out.
    //
    // Eventless is only half of it. This still returns a new state object, as
    // every engine result does, so a caller that snapshots and broadcasts on a
    // changed state will do both for a ping. The server keeps heartbeats off that
    // path entirely; see `Room.recordHeartbeat` in the backend.
    return { ok: true, state: patchPlayer(asEngine(state), playerId, { lastHeartbeat: now }), events: [] };
}
function rename(state, now, playerId, displayName) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    const checked = validateDisplayName(displayName);
    if (!checked.ok)
        return fail(checked.error, 'name-invalid');
    // Suffixed rather than refused, and the renamer is the one suffixed: whoever
    // is wearing the name keeps it. This used to `fail('name-taken')`, which told
    // a player their own name was unavailable on a screen that does not show them
    // who has it. The same rule runs on join, in `addPlayer`.
    const value = uniqueDisplayName(checked.value, Object.values(state.players)
        .filter((other) => other.id !== playerId)
        .map((other) => other.displayName));
    const draft = draftOf(state);
    draft.state = patchPlayer(draft.state, playerId, { displayName: value });
    pushLog(draft, 'player-joined', `${player.displayName} is now ${value}`, now);
    return done(draft);
}
/**
 * Attach a verified account to a seat that already exists.
 *
 * `add-player` writes `userId` and `username` once, at the moment the seat is
 * created, and until this action existed nothing could ever write them again. A
 * player who signed in after taking the seat — or who took it in the four
 * seconds the client gives Clerk's script to answer — was a guest for as long as
 * the room lived, across reloads and deploys, because the reconnect token in
 * localStorage kept finding the same seat.
 *
 * **It only ever upgrades.** The client re-sends its session token on every
 * socket.io reconnect and a Clerk session JWT lives about a minute, so the token
 * that arrives after a wifi blip is routinely one the server cannot verify.
 * Writing `userId = session?.userId ?? null` here would demote a signed-in
 * player to a guest on the first blip, and — since this is the only writer —
 * leave them one. A null session says "no news", never "signed out".
 *
 * Signing out for real is a different event and deliberately does not travel
 * this way. The seat belongs to whoever holds the reconnect token, and dropping
 * the account off it mid-game would take the game's attribution with it.
 */
function identify(state, playerId, userId, username) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.isBot)
        return fail('a bot has no account', 'invalid-action');
    // Nothing to say, or nothing new. Returning the input state rather than a copy
    // is what keeps a reconnect that carried no session out of the snapshot
    // writer, which compares the state object it was handed against the one it had.
    if (userId === null)
        return { ok: true, state, events: [] };
    if (player.userId === userId && player.username === username) {
        return { ok: true, state, events: [] };
    }
    const draft = draftOf(state);
    draft.state = patchPlayer(draft.state, playerId, { userId, username });
    return done(draft);
}
/**
 * Colour rules, in one place because both joining and re-picking need them.
 *
 * Teams own colour outright: while teams are on, a player renders in their
 * team's colour, so a player wearing a team's colour is a player nobody can
 * place. `PLAYER_COLORS` and `TEAM_COLORS` are disjoint by construction, and
 * this is what keeps a hand-picked colour inside that promise.
 */
function colorIsTeamOwned(color) {
    return TEAM_COLORS.some((team) => team.color === color);
}
function colorTakenBy(state, color, exceptId) {
    // `player.color` is nullable now and a null never equals a colour anybody can
    // ask for, so a room past the palette reports nothing as taken. That is the
    // right answer for the only caller that can reach it — nobody may pick a
    // colour there at all, and `setColor` refuses first.
    return Object.values(state.players).some((player) => player.id !== exceptId && player.color !== null && player.color === color);
}
/**
 * The first palette colour nobody in the room is wearing.
 *
 * Falls back to the palette entry the room has fewest of rather than to
 * `PLAYER_COLORS[0]`, so a room past the palette size shares colours out evenly
 * instead of dressing everybody after the eighth in the same blue.
 */
function freeColor(state, exceptId) {
    const counts = new Map();
    for (const color of PLAYER_COLORS)
        counts.set(color, 0);
    for (const player of Object.values(state.players)) {
        if (player.id === exceptId || player.color === null)
            continue;
        const seen = counts.get(player.color);
        if (seen !== undefined)
            counts.set(player.color, seen + 1);
    }
    let best = PLAYER_COLORS[0];
    let bestCount = Infinity;
    for (const color of PLAYER_COLORS) {
        const count = counts.get(color) ?? 0;
        if (count < bestCount) {
            best = color;
            bestCount = count;
        }
    }
    return best;
}
/** Whether a room with this many seats hands anybody a colour. */
function roomHasColors(seatCount) {
    return colorForSeat(0, seatCount) !== null;
}
/**
 * Settle who is wearing what, after anything that changes how many seats there
 * are.
 *
 * **Colour is all or nothing across a room and the line is a seat count.** Past
 * `COLORED_SEATS` nobody has one — not "the first sixteen keep theirs", which
 * would mark sixteen people out of five hundred for no reason a player could
 * name — so the seventeenth arrival takes colour away from everybody and the
 * seventeenth departure gives it back. `colorForSeat` is the one place that is
 * decided; this is the one place it is applied.
 *
 * What comes back is not what was taken away, and that is deliberate rather than
 * a shortcut. `ServerPlayer.color` is the only colour field there is, so a room
 * that crossed the line has genuinely forgotten who hand-picked what; inventing
 * a second "preferred colour" field to restore from would be state carried
 * across a boundary the room has already been told nothing survives. Coming back
 * under the line, seats are dressed by their position in `playerOrder` — the
 * index `colorForSeat` is documented against — and everybody gets a colour
 * nobody else has, which is the property that actually matters.
 *
 * Cheap and idempotent in the ordinary case: a room that is not crossing the
 * line returns the state object it was handed, so this can sit on every
 * membership path without churning the snapshot writer.
 */
function withResolvedColors(state) {
    const seats = seatedIds(state);
    const players = { ...state.players };
    let changed = false;
    if (!roomHasColors(seats.length)) {
        for (const [id, player] of Object.entries(players)) {
            if (player.color === null)
                continue;
            players[id] = { ...player, color: null };
            changed = true;
        }
        return changed ? { ...state, players } : state;
    }
    // **The audience wears no colour, and that is the whole of the second half of
    // this function now.** A colour is how the running order, the reveal and the
    // standing say who is who, and somebody watching is in none of those — so a
    // palette entry on the bench was an identity issued for a screen that never
    // draws it, taken out of the sixteen that the people playing have to share.
    // A room of twelve players and eight watchers ran out of colours for the
    // players.
    //
    // It is cleared rather than merely not assigned, because the way onto the
    // bench is `moveRole` from a seat that already had one.
    for (const [id, player] of Object.entries(players)) {
        if (player.role === 'player' || player.color === null)
            continue;
        players[id] = { ...player, color: null };
        changed = true;
    }
    // Under the line and a seat is bare, which means either the room has just come
    // back under it or somebody has just sat down. By seat index, so a full
    // sixteen is sixteen distinct colours.
    seats.forEach((id, index) => {
        const player = players[id];
        if (player === undefined || player.color !== null)
            return;
        // The seat's own colour unless somebody is already wearing it, which can
        // only happen if a bare seat and a dressed one coexist — they do not today,
        // because the only way to be bare is the whole room having been. The
        // fallback is here so that stays a fact rather than an assumption: two
        // players in one colour is the exact failure the palette exists to prevent.
        const byIndex = colorForSeat(index, seats.length);
        const taken = byIndex === null || colorTakenBy({ ...state, players }, byIndex, id);
        players[id] = { ...player, color: taken ? freeColor({ ...state, players }, id) : byIndex };
        changed = true;
    });
    return changed ? { ...state, players } : state;
}
/**
 * A player picking their own colour.
 *
 * Gated on `playerPermissions`, with the host always allowed: in a room of five
 * hundred it is one press between an ordinary lobby and somebody wearing the
 * colour of whoever is winning. See `GameSettings.playerPermissions`.
 *
 * Refused outright once the room is past `COLORED_SEATS`, where nobody has a
 * colour to change. Storing one anyway would leave a single player marked out of
 * five hundred the moment somebody left and the room came back under the line.
 */
function setColor(state, playerId, color) {
    if (state.players[playerId] === undefined)
        return fail('no such player', 'invalid-action');
    if (!mayUsePlayerPowers(state, playerId)) {
        return fail('the host is choosing the colours in this room', 'invalid-action');
    }
    if (!roomHasColors(seatedIds(state).length)) {
        return fail('this room is too big for colours', 'invalid-action');
    }
    if (colorIsTeamOwned(color)) {
        return fail('that colour belongs to a team', 'invalid-action');
    }
    if (colorTakenBy(state, color, playerId)) {
        return fail('somebody else is already that colour', 'invalid-action');
    }
    return done({ state: patchPlayer(asEngine(state), playerId, { color }), events: [] });
}
/**
 * Move one player between the seats and the bench, with no opinion about who
 * asked. The three callers check that for themselves, because they answer it
 * differently: `set-role` is a player choosing where they sit, `assign-role` is
 * the host doing the seating, and `leave-seat` is a player standing down from a
 * game already in progress.
 */
function moveRole(draft, playerId, role, patch) {
    const player = draft.state.players[playerId];
    if (player === undefined)
        return;
    draft.state = patchPlayer(draft.state, playerId, {
        role,
        waitingForNextGame: false,
        teamId: role === 'spectator' ? null : player.teamId,
        // Taking a seat again clears the mark, because a seat held is a seat held.
        // Standing down sets it, and `leaveSeat` passes it in.
        leftSeatAt: role === 'player' ? null : (player.leftSeatAt ?? null),
        ...(patch ?? {}),
    });
    draft.state = {
        ...draft.state,
        playerOrder: role === 'player'
            ? [...draft.state.playerOrder.filter((id) => id !== playerId), playerId]
            : draft.state.playerOrder.filter((id) => id !== playerId),
        readyIds: role === 'player'
            ? draft.state.readyIds
            : draft.state.readyIds.filter((id) => id !== playerId),
    };
    draft.state = withResolvedColors(draft.state);
}
function setRole(state, playerId, role) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.isBot)
        return fail('a bot cannot change seat', 'invalid-action');
    if (state.phase !== 'lobby') {
        return fail('seats change in the lobby only', 'invalid-state');
    }
    if (player.role === role)
        return done(draftOf(state));
    if (role === 'player' && roomIsFull(state)) {
        return fail('the room is full', 'room-full');
    }
    // The lock is the door, so it holds against somebody taking a seat for
    // themselves and not against the host seating them — `assign-role` is the
    // other half of it and deliberately does not consult this.
    if (role === 'player' && (state.settings.roomLock ?? 'open') !== 'open' && !isHost(state, playerId)) {
        return fail('this room is locked', 'invalid-action');
    }
    const draft = draftOf(state);
    moveRole(draft, playerId, role);
    return done(draft);
}
/**
 * The host moving somebody else between the seats and the bench.
 *
 * **The other half of `roomLocked`.** A lock puts arrivals on the bench, and
 * without a way back off it a streamer whose friend turned up thirty seconds
 * late has no way to seat them short of unlocking the room to the whole
 * audience. So this one ignores the lock: it is the host doing the seating, and
 * the lock exists to make sure they are the only one who can.
 *
 * The seat cap still applies. That is a number the host set on purpose and there
 * is no reason to let this walk past it — `update-settings` is where a host who
 * wants more seats goes.
 */
function assignRole(state, playerId, targetId, role) {
    if (!isHost(state, playerId))
        return fail('only the host may seat somebody', 'not-host');
    const target = state.players[targetId];
    if (target === undefined)
        return fail('no such player', 'invalid-action');
    if (target.isBot)
        return fail('a bot cannot change seat', 'invalid-action');
    if (state.phase !== 'lobby') {
        return fail('seats change in the lobby only', 'invalid-state');
    }
    if (target.role === role)
        return done(draftOf(state));
    if (role === 'player' && roomIsFull(state)) {
        return fail('the room is full', 'room-full');
    }
    const draft = draftOf(state);
    moveRole(draft, targetId, role);
    return done(draft);
}
/**
 * A seated player standing down MID-GAME, which `set-role` refuses.
 *
 * **Not the same as leaving, and the difference is what it keeps.**
 * `remove-player` takes the seat's answers with it, so somebody who has to go
 * three rounds into a fifteen-round game erases everything they played. This
 * keeps every round they already played — the rounds are judged and are history
 * — scores every remaining one zero, and leaves them in the room watching.
 *
 * Two details do the work.
 *
 * **Their answer to the round in flight goes.** It is unjudged, so keeping it
 * would score somebody who is no longer playing, and dropping it is what stops
 * the reveal drawing an entry for them: `revealOrder` is built from
 * `round.submissions`, so a seat with nothing in it produces no row rather than
 * a dash. That matters more than it sounds — a room of two hundred watching an
 * absent seat's dash every round for twelve rounds is the thing this action
 * exists to prevent, and `revealStepCount` would be paying for it a beat at a
 * time.
 *
 * **`leftSeatAt` keeps them in the standing.** Their role is `spectator`, so
 * nothing asks them for an answer and the chooser rotation skips them, but
 * `seatScoresThisGame` is what every total in `scoring.ts` reads, and it says
 * they are still in this game at whatever they had. Dropping them out of the
 * standing would delete points the room watched them score, which is precisely
 * what "keeps every round they already played" rules out.
 *
 * They keep no claim on the seat: coming back is `set-role`, in the lobby, like
 * anybody else, and that clears the mark.
 */
function leaveSeat(state, now, playerId) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.isBot)
        return fail('a bot cannot stand down', 'invalid-action');
    if (player.role !== 'player')
        return fail('you are not in a seat', 'invalid-action');
    // In the lobby there is no game to keep half of, and `set-role` is the plain
    // way to do it. Refusing here rather than quietly aliasing the two keeps
    // `leftSeatAt` meaning what it says: somebody stood down from a game that was
    // being played.
    if (state.phase === 'lobby') {
        return fail('use the seat control in the lobby', 'invalid-state');
    }
    const draft = draftOf(state);
    // The round in flight only. A judged round is what they played and it stays,
    // answers, score and all.
    const round = currentRound(draft.state);
    if (round !== null && round.revealedAt === null) {
        const submissions = { ...round.submissions };
        delete submissions[playerId];
        draft.state = replaceRound(draft.state, patchRound(round, { submissions }));
    }
    moveRole(draft, playerId, 'spectator', { leftSeatAt: now });
    // Their earlier rounds still count and the totals are a pure function of the
    // rounds, so this simply re-reads them — what changes is that the round in
    // flight no longer holds an answer of theirs.
    draft.state = recomputeScores(draft.state);
    pushLog(draft, 'player-left', `${player.displayName} stood down from their seat`, now);
    return done(draft);
}
/**
 * The longest-connected remaining player: earliest `joinedAt` among the
 * connected. Seated players first, then spectators, and never a bot.
 */
function hostSuccessor(state) {
    const candidates = Object.values(state.players).filter((player) => player.connected && !player.isBot && player.id !== state.hostId);
    if (candidates.length === 0)
        return null;
    candidates.sort((a, b) => {
        const seatDelta = Number(b.role === 'player') - Number(a.role === 'player');
        if (seatDelta !== 0)
            return seatDelta;
        if (a.joinedAt !== b.joinedAt)
            return a.joinedAt - b.joinedAt;
        return a.id < b.id ? -1 : 1;
    });
    return candidates[0].id;
}
function handoffHost(draft, now, automatic) {
    const successor = hostSuccessor(draft.state);
    if (successor === null)
        return;
    draft.state = { ...draft.state, hostId: successor };
    draft.events.push({ type: 'host-transferred', hostId: successor, automatic });
    pushLog(draft, 'host-transferred', `${draft.state.players[successor].displayName} is now the host`, now);
}
function transferHost(state, now, playerId, automatic) {
    const target = state.players[playerId];
    if (target === undefined)
        return fail('no such player', 'invalid-action');
    if (target.isBot)
        return fail('a bot cannot host', 'invalid-action');
    if (state.hostId === playerId)
        return done(draftOf(state));
    const draft = draftOf(state);
    draft.state = { ...draft.state, hostId: playerId };
    draft.events.push({ type: 'host-transferred', hostId: playerId, automatic });
    pushLog(draft, 'host-transferred', `${target.displayName} is now the host`, now);
    return done(draft);
}
function setMuted(state, now, playerId, muted) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    const draft = draftOf(state);
    draft.state = patchPlayer(draft.state, playerId, { muted });
    pushLog(draft, 'player-muted', `${player.displayName} was ${muted ? 'muted' : 'unmuted'}`, now);
    return done(draft);
}
// ---------------------------------------------------------------------------
// Lobby
// ---------------------------------------------------------------------------
/**
 * The host changing the room's settings.
 *
 * **Almost all of them are lobby-only, and the exceptions are named rather than
 * guessed at.** A room that can retune the round clock mid-game can retune it in
 * response to how the round is going, which is why the rule is what it is.
 * `SAFETY_SETTINGS` is the list it does not apply to, and every key on it is a
 * control a host reaches for when something is going wrong in the room — chat
 * off, the room locked, permissions withdrawn — none of which changes what
 * anybody scores. The moment one of those is needed is by definition not the
 * lobby.
 *
 * Outside the lobby the patch is NARROWED to that list rather than refused
 * whole. A client sending its entire settings object to change one of them is
 * doing the ordinary thing, and refusing the lot because a round clock came
 * along unchanged would make the safety controls unreachable from exactly the
 * client most likely to be a version behind. Nothing is silently accepted: what
 * is dropped is dropped, and a patch with nothing left in it is refused.
 */
function updateSettings(state, now, playerId, patch) {
    if (!isHost(state, playerId))
        return fail('only the host may change settings', 'not-host');
    let requested = patch;
    if (state.phase !== 'lobby') {
        const safe = {};
        for (const key of SAFETY_SETTINGS) {
            if (key in patch)
                safe[key] = patch[key];
        }
        if (Object.keys(safe).length === 0) {
            return fail('settings change in the lobby only', 'invalid-state');
        }
        requested = safe;
    }
    const clean = sanitizeSettings(requested, state.settings);
    const merged = { ...state.settings, ...clean };
    const checked = validateSettings(merged);
    if (!checked.ok)
        return fail(checked.error, 'invalid-action');
    const draft = draftOf(state);
    draft.state = { ...draft.state, settings: merged };
    // No teams-off branch here any more. `teamsEnabled` was a second copy of a
    // fact `state.teams` already held, so switching it off had to reach into every
    // player's `teamId` and empty the roster — undoing a thing the room had done
    // by hand, from a control that was really only ever a label. Teams are on
    // precisely while teams exist; `deleteTeam` is how a room stops playing them.
    // See `teamsAreOn`.
    //
    // A cap lowered below the seats already taken does not evict anybody either,
    // deliberately: `maxPlayers` is checked on the way in, and a setting that
    // could throw somebody out of a room they are sitting in is a different and
    // much sharper thing than a door policy.
    draft.events.push({ type: 'settings-updated', settings: merged });
    pushLog(draft, 'settings-changed', 'settings were changed', now);
    return done(draft);
}
function setReady(state, playerId, ready) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.role !== 'player')
        return fail('spectators do not ready up', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('ready up in the lobby only', 'invalid-state');
    const draft = draftOf(state);
    const without = draft.state.readyIds.filter((id) => id !== playerId);
    draft.state = { ...draft.state, readyIds: ready ? [...without, playerId] : without };
    return done(draft);
}
/** Members of a team, in seat order. */
function membersOf(state, teamId) {
    return state.playerOrder.filter((id) => state.players[id]?.teamId === teamId);
}
/**
 * Open the next unused team from the palette.
 *
 * The palette is fixed and ordered, so which team appears is a pure function of
 * which ones already exist: no randomness, no name to validate, and two players
 * pressing the button at the same moment get two different teams rather than one
 * team twice.
 *
 * **Gated on `playerPermissions`, and that default is a reversal.** Anybody used
 * to be able to, on the reasoning that a team is a shared container and whoever
 * wants one should make one. That holds for six friends and collapses for a
 * streamer playing five hundred viewers, where it is one button between an
 * ordinary lobby and four teams nobody asked for. The host can open it in a
 * press. Nothing about the flag it used to flip survives: opening a team IS
 * teams being on. See `teamsAreOn`.
 */
function createTeam(state, now, playerId) {
    if (state.players[playerId] === undefined)
        return fail('no such player', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('teams are formed in the lobby only', 'invalid-state');
    if (!mayUsePlayerPowers(state, playerId)) {
        return fail('only the host may open a team in this room', 'invalid-action');
    }
    const next = TEAM_COLORS.find((team) => !state.teams.some((existing) => existing.id === team.id));
    if (next === undefined)
        return fail('every team already exists', 'invalid-action');
    const draft = draftOf(state);
    draft.state = { ...draft.state, teams: [...draft.state.teams, { ...next }] };
    draft.events.push({ type: 'team-created', team: { ...next } });
    pushLog(draft, 'settings-changed', `${next.name} was opened as a team`, now);
    return done(draft);
}
/**
 * Delete an EMPTY team. A team with members is not deletable this way — move
 * them out first, which is a deliberate act somebody has to take.
 *
 * Gated the same way `createTeam` is, and for the same reason: closing three
 * teams somebody else opened is the same size of change to the lobby as opening
 * them. Closing the last one is how a room stops playing in teams — there is no
 * flag to clear, because there is no flag.
 */
function deleteTeam(state, now, playerId, teamId) {
    if (state.players[playerId] === undefined)
        return fail('no such player', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('teams are formed in the lobby only', 'invalid-state');
    if (!mayUsePlayerPowers(state, playerId)) {
        return fail('only the host may close a team in this room', 'invalid-action');
    }
    const team = state.teams.find((candidate) => candidate.id === teamId);
    if (team === undefined)
        return fail('no such team', 'invalid-action');
    if (membersOf(state, teamId).length > 0) {
        return fail('that team still has players in it', 'invalid-action');
    }
    const draft = draftOf(state);
    draft.state = {
        ...draft.state,
        teams: draft.state.teams.filter((candidate) => candidate.id !== teamId),
    };
    draft.events.push({ type: 'team-deleted', teamId });
    pushLog(draft, 'settings-changed', `${team.name} was closed`, now);
    return done(draft);
}
/**
 * Somebody held the room open.
 *
 * The engine does almost nothing here on purpose. How long a room may sit idle,
 * and whether it is eligible to be reclaimed at all, is the host process's
 * resource policy rather than a rule of Countdown — it lives on the `Room`
 * object next to the socket map, and this action resets it by the ordinary rule
 * that anything outside `IDLE_EXEMPT_ACTIONS` counts as somebody doing
 * something. What belongs in state is the fact that it happened, which is the
 * log line: a room is entitled to know why it is still here, and to see that
 * nobody pressed anything before it went.
 *
 * **Anybody present may.** No host check — the host is the likeliest person to
 * be away from the keyboard, and a room that can only be saved by the one player
 * who has gone is not saveable. No role check either: a spectator is a person in
 * the room. A bot is refused, because a seat that cannot press a button must not
 * be able to hold a room open on everyone else's behalf, and because it would
 * turn a room full of bots into a room with no expiry at all.
 */
function extendRoom(state, now, playerId) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.isBot)
        return fail('a bot cannot hold a room open', 'invalid-action');
    const draft = draftOf(state);
    pushLog(draft, 'room-extended', `${player.displayName} kept the room open`, now);
    return done(draft);
}
/**
 * Put a player on a team, or take them off one.
 *
 * `actorId` is who asked and `playerId` is whose team is changing. They are the
 * same person for the ordinary case and differ when the host is doing the
 * seating, which is the only reason the engine needs both: **`allowTeamSelfSelect`
 * gates one and not the other.** Making a team is a change to the shape of the
 * lobby; joining one that already exists is choosing where to sit in it, and a
 * host who has opened four teams has already said the teams are there to be
 * joined — so this defaults ON where `playerPermissions` defaults off. A host
 * who wants to do the seating turns it off and places people themselves, which
 * is exactly the case that would break if the gate could not tell the two apart.
 */
function assignTeam(state, now, playerId, teamId, actorId) {
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (state.phase !== 'lobby') {
        // Mid-game team formation would make best-of scoring incoherent.
        return fail('teams are formed in the lobby only', 'invalid-state');
    }
    if (actorId === playerId &&
        state.settings.allowTeamSelfSelect === false &&
        !isHost(state, actorId)) {
        return fail('the host is picking the teams in this room', 'invalid-action');
    }
    if (actorId !== playerId && !isHost(state, actorId)) {
        return fail('only the host may put somebody else on a team', 'not-host');
    }
    if (player.role !== 'player')
        return fail('spectators have no team', 'invalid-action');
    if (teamId === null) {
        return done({ state: patchPlayer(asEngine(state), playerId, { teamId }), events: [] });
    }
    const known = TEAM_COLORS.some((team) => team.id === teamId);
    if (!known)
        return fail('no such team', 'invalid-action');
    const draft = draftOf(state);
    // Joining a palette team that nobody has opened yet opens it. Refusing instead
    // would mean a client had to create-then-join and handle the gap between them,
    // and the outcome is identical either way.
    //
    // **Which is exactly why it has to carry `createTeam`'s gate.** Opening a team
    // through the back door is still opening a team, and with self-selection on by
    // default and permissions off by default, a join that could open one would let
    // any of five hundred viewers make four teams one membership at a time —
    // undoing the whole reason `playerPermissions` defaults to off.
    if (!draft.state.teams.some((team) => team.id === teamId)) {
        if (!mayUsePlayerPowers(state, actorId)) {
            return fail('only the host may open a team in this room', 'invalid-action');
        }
        const team = TEAM_COLORS.find((candidate) => candidate.id === teamId);
        draft.state = { ...draft.state, teams: [...draft.state.teams, { ...team }] };
        draft.events.push({ type: 'team-created', team: { ...team } });
        pushLog(draft, 'settings-changed', `${team.name} was opened as a team`, now);
    }
    draft.state = patchPlayer(draft.state, playerId, { teamId });
    return done(draft);
}
function addBot(state, now, botId, name, color, difficulty, botKind = 'heuristic', botModel = null, botReasoning = false, botOwnerUserId = null) {
    if (state.phase !== 'lobby')
        return fail('bots join in the lobby only', 'invalid-state');
    if (state.players[botId] !== undefined)
        return fail('that bot id is taken', 'invalid-action');
    if (roomIsFull(state)) {
        return fail('the room is full', 'room-full');
    }
    const draft = draftOf(state);
    const bot = {
        id: botId,
        userId: null,
        username: null,
        displayName: name,
        deviceId: null,
        color: color.length > 0 && !colorIsTeamOwned(color) && !colorTakenBy(state, color, botId)
            ? color
            : freeColor(state, botId),
        isBot: true,
        botKind,
        botDifficulty: difficulty,
        // An LLM bot with no model is not a thing the server should be able to
        // create; it is refused before dispatch. Storing null here anyway means a
        // bad caller produces a bot that never answers rather than one that
        // crashes the scheduler mid-round.
        botModel: botKind === 'llm' ? botModel : null,
        // `reasoningAllowed` and not the raw argument: the switch is off, so this
        // lands false whatever a caller sent. See `REASONING_AVAILABLE`.
        botReasoning: botKind === 'llm' ? reasoningAllowed(botReasoning) : false,
        role: 'player',
        leftSeatAt: null,
        teamId: null,
        score: 0,
        connected: true,
        lastHeartbeat: now,
        joinedAt: now,
        disconnectedAt: null,
        muted: false,
        waitingForNextGame: false,
        reconnectToken: '',
        ip: null,
        botOwnerUserId: botKind === 'llm' ? botOwnerUserId : null,
    };
    draft.state = {
        ...draft.state,
        players: { ...draft.state.players, [botId]: bot },
        playerOrder: [...draft.state.playerOrder, botId],
    };
    draft.state = withResolvedColors(draft.state);
    draft.events.push({
        type: 'player-joined',
        player: publicPlayerOf(draft.state, draft.state.players[botId]),
    });
    pushLog(draft, 'player-joined', `${name} joined`, now);
    return done(draft);
}
function removeBot(state, now, playerId) {
    const bot = state.players[playerId];
    if (bot === undefined || !bot.isBot)
        return fail('no such bot', 'invalid-action');
    return removePlayer(state, now, playerId);
}
function setBotDifficulty(state, playerId, difficulty) {
    const bot = state.players[playerId];
    if (bot === undefined || !bot.isBot)
        return fail('no such bot', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('bots change in the lobby only', 'invalid-state');
    return done({ state: patchPlayer(asEngine(state), playerId, { botDifficulty: difficulty }), events: [] });
}
/**
 * Point an LLM bot at a different model.
 *
 * Refuses a heuristic bot rather than quietly converting it: the two answer by
 * completely different machinery, and a silent switch would leave a player
 * looking at a "medium" badge on something spending money.
 *
 * Converting one *is* a thing the host can do now — `set-bot-kind` below — and
 * that does not soften this refusal, it is the reason for it. A conversion is
 * an act the host chooses, gated on being signed in and on there being budget
 * left. Letting a repoint imply one would have been the same spend arriving
 * through a door with no gate on it.
 */
function setBotModel(state, playerId, model, reasoning) {
    const bot = state.players[playerId];
    if (bot === undefined || !bot.isBot)
        return fail('no such bot', 'invalid-action');
    if (bot.botKind !== 'llm')
        return fail('that opponent is not an LLM', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('bots change in the lobby only', 'invalid-state');
    return done({
        state: patchPlayer(asEngine(state), playerId, {
            botModel: model,
            botReasoning: reasoningAllowed(reasoning),
        }),
        events: [],
    });
}
/**
 * Convert a seated bot between scripted and AI, in place.
 *
 * What makes this worth an action of its own rather than a remove-and-re-add is
 * everything it does *not* touch. The bot keeps its id, so a game record and a
 * chat log still point at the same seat; its display name, so the room is not
 * told somebody left and somebody else arrived; its colour, its score, and its
 * place in `playerOrder`, which is the running order and the chooser rotation.
 *
 * **Difficulty always lands on a real value, on both sides of the conversion.**
 * `botDifficulty` is not a scripted-bot-only field: a bot chooser calls its
 * tiles and picks its large count at that difficulty whether it is scripted or
 * an LLM, so an AI with a null difficulty would be a chooser with nothing to
 * choose by. Converting keeps whatever the bot already had unless the caller
 * names one, and falls back to `medium` for the bot old enough to have none.
 *
 * The lobby-only rule is the same one `set-bot-difficulty` and `set-bot-model`
 * carry, and here it also does the arming: a bot's turn is armed on arrival at
 * a phase, so a conversion that could land mid-round would leave a converted
 * opponent unarmed for the round it happened in. It cannot, because there is no
 * round yet.
 */
function setBotKind(state, playerId, botKind, difficulty, model, reasoning, ownerUserId) {
    const bot = state.players[playerId];
    if (bot === undefined || !bot.isBot)
        return fail('no such bot', 'invalid-action');
    if (state.phase !== 'lobby')
        return fail('bots change in the lobby only', 'invalid-state');
    if (botKind === null)
        return fail('a bot needs a kind', 'invalid-action');
    // The server supplies its default before this is dispatched, so reaching here
    // empty means a caller went round it. Refusing beats seating an opponent that
    // never answers.
    if (botKind === 'llm' && (model === null || model === '')) {
        return fail('an AI opponent needs a model', 'invalid-action');
    }
    const landed = difficulty ?? bot.botDifficulty ?? 'medium';
    const patch = botKind === 'llm'
        ? {
            botKind: 'llm',
            botDifficulty: landed,
            botModel: model,
            botReasoning: reasoningAllowed(reasoning),
            botOwnerUserId: ownerUserId,
        }
        : {
            // Back to scripted, and the model goes with it. A stale id left on a
            // heuristic bot would show up on its chip as a model it is not
            // playing, and `botsYetToAnswer` reads `botKind` alone.
            botKind: 'heuristic',
            botDifficulty: landed,
            botModel: null,
            botReasoning: false,
            botOwnerUserId: null,
        };
    return done({ state: patchPlayer(asEngine(state), playerId, patch), events: [] });
}
// ---------------------------------------------------------------------------
// Round shells and the round-intro hold
// ---------------------------------------------------------------------------
function emptyRoundOf(type, index, chooserId, clock) {
    const base = {
        index,
        chooserId,
        clock,
        submissions: {},
        spectatorSubmissions: {},
        best: null,
        startedAt: null,
        revealedAt: null,
        revealCursor: 0,
        nextRevealAt: null,
        revealAdvanceAt: null,
    };
    switch (type) {
        case 'letters':
            return { ...base, type: 'letters', tiles: [], vowelPile: [], consonantPile: [] };
        case 'numbers':
            return { ...base, type: 'numbers', tiles: [], largeCount: null, target: null };
        case 'conundrum':
            return {
                ...base,
                type: 'conundrum',
                scramble: '',
                solution: '',
                buzzedBy: null,
                buzzClock: null,
                lockedOut: [],
                solvedBy: null,
            };
    }
}
/**
 * Enter `round-intro` for `index`, creating the round shell and burning one step
 * of the chooser rotation. `round.clock` holds the intro hold, so the server's
 * timer wakes up exactly when it should send `begin-round`.
 */
function enterRoundIntro(draft, index, now) {
    const type = draft.state.settings.roundSequence[index];
    const chooserId = nextChooserId(draft.state);
    const round = emptyRoundOf(type, index, chooserId, startClock(ROUND_INTRO_MS, now));
    draft.state = {
        ...draft.state,
        currentRoundIndex: index,
        chooserRotationIndex: draft.state.chooserRotationIndex + 1,
        rounds: [...draft.state.rounds.filter((existing) => existing.index !== index), round],
    };
    setPhase(draft, 'round-intro');
    draft.events.push({ type: 'round-intro', roundIndex: index, roundType: type, chooserId });
}
function startGame(state, now, gameId) {
    // No actor rides on `start-game`, so the host check lives in the server. The
    // engine enforces everything it can see: lobby, a seated player, a sane plan.
    if (state.phase !== 'lobby')
        return fail('a game is already running', 'game-in-progress');
    if (seatedIds(state).length === 0)
        return fail('nobody is seated', 'invalid-action');
    const sequence = state.settings.roundSequence;
    if (sequence.length === 0)
        return fail('the round sequence is empty', 'invalid-action');
    if (sequence.length > ROUND_SEQUENCE_MAX) {
        return fail(`at most ${ROUND_SEQUENCE_MAX} rounds`, 'invalid-action');
    }
    const checked = validateSettings(state.settings);
    if (!checked.ok)
        return fail(checked.error, 'invalid-action');
    const draft = draftOf(state);
    const players = { ...draft.state.players };
    for (const id of Object.keys(players)) {
        // `leftSeatAt` goes with the score. It says "this spectator is still in the
        // game that was running", and the game that was running is over — left set,
        // it would put somebody who stood down last game into this game's standing
        // at zero, for ever.
        players[id] = { ...players[id], score: 0, leftSeatAt: null };
    }
    draft.state = {
        ...draft.state,
        gameId,
        players,
        startedAt: now,
        endedAt: null,
        winnerIds: [],
        rounds: [],
        readyIds: [],
    };
    pushLog(draft, 'game-started', 'the game started', now);
    enterRoundIntro(draft, 0, now);
    return done(draft);
}
function beginRound(state, now, setup) {
    if (state.phase !== 'round-intro')
        return fail('not waiting for a round', 'invalid-state');
    const round = currentRound(state);
    if (round === null)
        return fail('no round to begin', 'invalid-state');
    if (round.type !== setup.type) {
        return fail(`round ${round.index} is a ${round.type} round`, 'invalid-action');
    }
    const draft = draftOf(state);
    const settings = draft.state.settings;
    switch (setup.type) {
        case 'letters': {
            const letters = round;
            draft.state = replaceRound(draft.state, {
                ...letters,
                tiles: [],
                vowelPile: [...setup.vowelPile],
                consonantPile: [...setup.consonantPile],
                startedAt: now,
                clock: startClock(settings.lettersSelectionSeconds * 1000, now),
            });
            setPhase(draft, 'letters-selection');
            break;
        }
        case 'numbers': {
            const numbers = round;
            draft.state = replaceRound(draft.state, {
                ...numbers,
                tiles: [],
                largeCount: null,
                target: null,
                startedAt: now,
                clock: startClock(settings.numbersSelectionSeconds * 1000, now),
            });
            setPhase(draft, 'numbers-selection');
            break;
        }
        case 'conundrum': {
            const conundrum = round;
            draft.state = replaceRound(draft.state, {
                ...conundrum,
                scramble: setup.scramble,
                solution: setup.solution,
                lockedOut: [],
                buzzedBy: null,
                buzzClock: null,
                solvedBy: null,
                // Captured now rather than read from the settings at scoring time. See
                // `ConundrumRound.buzzerOff`: `SAFETY_SETTINGS` can be patched mid-game,
                // and a round is scored by the rules it was dealt under.
                buzzerOff: settings.conundrumBuzzer === false,
                startedAt: now,
                clock: startClock(settings.conundrumSeconds * 1000, now),
            });
            setPhase(draft, 'conundrum-play');
            break;
        }
    }
    pushLog(draft, 'round-started', `round ${round.index + 1} (${round.type}) started`, now);
    return done(draft);
}
// ---------------------------------------------------------------------------
// Letters selection
// ---------------------------------------------------------------------------
function pickLetter(state, now, playerId, kind) {
    if (state.phase !== 'letters-selection')
        return fail('not picking letters', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'letters')
        return fail('no letters round', 'invalid-state');
    if (round.chooserId !== playerId)
        return fail('it is not your pick', 'not-your-turn');
    if (round.tiles.length >= LETTERS_TILE_COUNT) {
        return fail('the board is full', 'invalid-action');
    }
    if (!canPick(round.tiles, kind)) {
        const allowed = allowedPicks(round.tiles);
        return fail(allowed.length === 0
            ? 'no pick is legal here'
            : `only ${allowed.join(' or ')} is legal now, to keep the minimums reachable`, 'invalid-action');
    }
    if (round.clock === null)
        return fail('the selection clock is missing', 'invalid-state');
    // The piles live in state and may be frozen, so copy before `drawLetter` pops.
    const vowelPile = [...round.vowelPile];
    const consonantPile = [...round.consonantPile];
    const letter = drawLetter(kind === 'vowel' ? vowelPile : consonantPile);
    if (letter === null)
        return fail(`the ${kind} pile is empty`, 'invalid-action');
    const draft = draftOf(state);
    const tile = { letter, kind };
    const tiles = [...round.tiles, tile];
    // The clock is not touched. It used to be extended by `TILE_FLIP_MS` on every
    // pick, on the argument that the caller should not be charged for the board
    // being dealt — which cost the room nothing but made the disc do something a
    // clock must never do. `extendClock` moves the deadline OUT, so `remaining /
    // duration` stepped UP on every press, and the only way to draw that without
    // the wedge growing was to stop the disc dead for the length of every flip.
    // Nine little stops in a thirty-second round read as a broken timer, which is
    // what Isaac saw and reported as one. The budget is now simply thirty seconds
    // of wall clock, the disc only ever falls, and the four seconds the flips take
    // are four seconds of the caller's own.
    let next = {
        ...round,
        tiles,
        vowelPile,
        consonantPile,
    };
    draft.state = replaceRound(draft.state, next);
    draft.events.push({ type: 'letter-revealed', tileIndex: tiles.length - 1, tile });
    if (tiles.length === LETTERS_TILE_COUNT) {
        next = { ...next, clock: startClock(draft.state.settings.lettersPlaySeconds * 1000, now) };
        draft.state = replaceRound(draft.state, next);
        setPhase(draft, 'letters-play');
    }
    return done(draft);
}
function autoFill(state, now) {
    if (state.phase !== 'letters-selection')
        return fail('not picking letters', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'letters')
        return fail('no letters round', 'invalid-state');
    if (round.tiles.length >= LETTERS_TILE_COUNT)
        return fail('the board is full', 'invalid-action');
    const vowelPile = [...round.vowelPile];
    const consonantPile = [...round.consonantPile];
    // Deterministic chooser: the piles were already shuffled by the server, so
    // this only decides which legal kind to take next, and `applyAction` keeps its
    // promise never to touch Math.random.
    const random = seededRandom(now + vowelPile.length * 31 + consonantPile.length * 17 + round.index);
    const tiles = autoFillLetters(round.tiles, vowelPile, consonantPile, random);
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        tiles,
        vowelPile,
        consonantPile,
        clock: startClock(draft.state.settings.lettersPlaySeconds * 1000, now),
    });
    draft.events.push({ type: 'letters-auto-filled', tiles });
    setPhase(draft, 'letters-play');
    return done(draft);
}
// ---------------------------------------------------------------------------
// Numbers selection
// ---------------------------------------------------------------------------
/**
 * "Two large and four small, please" — one decision, and then the board deals
 * itself.
 *
 * This used to carry the finished board: the count, all six tiles and the
 * target, in one action, straight to `numbers-play`. The chooser pressed a
 * button and a complete round appeared between frames, which is not how the
 * show works and not how the letters round works either. It now sets the count
 * and nothing else, and hands over to `numbers-drawing`.
 *
 * The clock it starts is the deal pacing, not a budget anybody is spending. The
 * play clock does not begin until the target lands — the deal is roughly five
 * seconds and charging the room for watching it would be the letters round's
 * old `extendClock` mistake wearing different clothes, except worse, because
 * here nobody chose to spend it.
 */
function chooseLargeCount(state, now, playerId, largeCount) {
    if (state.phase !== 'numbers-selection')
        return fail('not choosing numbers', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'numbers')
        return fail('no numbers round', 'invalid-state');
    if (round.clock === null)
        return fail('the selection clock is missing', 'invalid-state');
    // Once the budget is gone the server picks the default on the chooser's
    // behalf, so a late arrival from anyone is accepted.
    const budgetGone = isExpired(round.clock, now);
    if (!budgetGone && round.chooserId !== playerId) {
        return fail('it is not your call', 'not-your-turn');
    }
    if (!Number.isInteger(largeCount) ||
        largeCount < NUMBERS_MIN_LARGE ||
        largeCount > NUMBERS_MAX_LARGE) {
        return fail(`large count must be ${NUMBERS_MIN_LARGE} to ${NUMBERS_MAX_LARGE}`, 'invalid-action');
    }
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        largeCount,
        clock: startClock(NUMBERS_DEAL_MS, now),
    });
    setPhase(draft, 'numbers-drawing');
    return done(draft);
}
/**
 * One number onto the board.
 *
 * The engine checks the tile is legal *for this board* and does not draw it:
 * `applyAction` never calls `Math.random`, so the server picks and this
 * validates. What it can check is real — a tile must be a large one when large
 * slots are still owed and a small one when they are not, and the piles are
 * finite, so a large already up cannot come up twice and no small can appear a
 * third time.
 */
function revealNumberTile(state, now, tile) {
    if (state.phase !== 'numbers-drawing')
        return fail('the board is not being dealt', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'numbers')
        return fail('no numbers round', 'invalid-state');
    if (round.largeCount === null)
        return fail('no large count was called', 'invalid-state');
    if (round.tiles.length >= NUMBERS_TILE_COUNT)
        return fail('the board is full', 'invalid-action');
    if (!isDrawableNumberTile(round.tiles, round.largeCount, tile)) {
        return fail('that tile cannot come off this board', 'invalid-action');
    }
    // **The board fills from the RIGHT, so `tiles` is display order and the newest
    // tile goes to the front.** Dealing appends in the obvious way and used to,
    // which put the last tile dealt on the right-hand end of the board.
    //
    // Isaac asked for the opposite and both halves of the ask are one thing: the
    // large tiles should come last, and they should end up leftmost. That is only
    // consistent if the deal runs right to left, so the tile dealt first sits at
    // the far right and each new one lands to the left of it.
    //
    // `tiles` stays the order everybody SEES — the board mid-deal, the board in
    // play, and `numbersTiles` on the permanent record. Storing deal order here
    // and reversing in the UI would make the record page disagree with what the
    // room watched, which is the sort of thing nobody notices until they do.
    //
    // Every rule in `isDrawableNumberTile` counts tiles rather than reading
    // positions, so it is indifferent to which end they arrive at.
    const tiles = [tile, ...round.tiles];
    const full = tiles.length === NUMBERS_TILE_COUNT;
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        tiles,
        // The last tile buys the long beat: the target spin is the only moment in a
        // numbers round where the whole room is waiting on the same thing.
        clock: startClock(full ? NUMBERS_TARGET_SPIN_MS : NUMBERS_DEAL_MS, now),
    });
    // The slot this tile just took, counted from the left of a full board, so a
    // client can place it without knowing how many are still to come.
    draft.events.push({ type: 'number-tile-revealed', tile, index: NUMBERS_TILE_COUNT - tiles.length });
    return done(draft);
}
/**
 * The target, and with it the round.
 *
 * It arrives on its own action, after the tiles and a beat, because the client
 * spends that beat spinning through three-digit numbers of its own invention.
 * Sending the real one early would put it on the wire before the room is meant
 * to have it, and the spin would then be a decoration over a value the network
 * tab already knows — which is the whole thing this shape exists to avoid.
 */
function revealNumberTarget(state, now, target) {
    if (state.phase !== 'numbers-drawing')
        return fail('the board is not being dealt', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'numbers')
        return fail('no numbers round', 'invalid-state');
    if (round.tiles.length !== NUMBERS_TILE_COUNT) {
        return fail('the board is not full yet', 'invalid-action');
    }
    if (!Number.isInteger(target) || target < NUMBERS_TARGET_MIN || target > NUMBERS_TARGET_MAX) {
        return fail(`the target must be ${NUMBERS_TARGET_MIN} to ${NUMBERS_TARGET_MAX}`, 'invalid-action');
    }
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        target,
        // **The phase does not change here, and the play clock does not start.**
        // The target used to land and the play screen replaced it in the same
        // frame, so the number the room had just spent two and a half seconds
        // waiting for was gone before anybody had read it. The payoff of a
        // countdown is the moment it stops, and it needs somewhere to land. This
        // clock is that beat; `begin-numbers-play` ends it.
        clock: startClock(NUMBERS_TARGET_HOLD_MS, now),
    });
    draft.events.push({ type: 'numbers-revealed', tiles: [...round.tiles], target });
    return done(draft);
}
/**
 * The beat after the target is up, and the round becoming playable.
 *
 * A separate action rather than a branch inside `reveal-number-target` because
 * the engine cannot wait: it is a pure function of state and an action, so
 * every pause in this game is a clock somebody else comes back for. The server
 * comes back for this one through `effectsFor`, the same way it comes back for
 * each tile.
 */
function beginNumbersPlay(state, now) {
    if (state.phase !== 'numbers-drawing')
        return fail('the board is not being dealt', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'numbers')
        return fail('no numbers round', 'invalid-state');
    if (round.target === null)
        return fail('the target has not landed', 'invalid-state');
    if (round.tiles.length !== NUMBERS_TILE_COUNT) {
        return fail('the board is not full yet', 'invalid-action');
    }
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        clock: startClock(draft.state.settings.numbersPlaySeconds * 1000, now),
    });
    setPhase(draft, 'numbers-play');
    return done(draft);
}
/**
 * Could this number legally be the next one off the board?
 *
 * Exported because the SERVER draws with it too — it picks from the set this
 * accepts rather than picking freely and hoping — so the rule about what the
 * piles hold exists once rather than twice. A second copy on the drawing side
 * is exactly how a board ends up with three 7s.
 */
export function isDrawableNumberTile(dealt, largeCount, tile) {
    const isLarge = NUMBERS_LARGE_TILES.includes(tile);
    const largeDealt = dealt.filter((t) => NUMBERS_LARGE_TILES.includes(t)).length;
    const slotsLeft = NUMBERS_TILE_COUNT - dealt.length;
    const largeOwed = largeCount - largeDealt;
    if (isLarge) {
        if (largeOwed <= 0)
            return false;
        // Each large exists once.
        return !dealt.includes(tile);
    }
    if (!NUMBERS_SMALL_TILES.includes(tile))
        return false;
    // Every remaining slot is spoken for by a large, so no small may take one.
    if (slotsLeft <= largeOwed)
        return false;
    // Two of each small, and no more.
    const copies = NUMBERS_SMALL_TILES.filter((t) => t === tile).length;
    return dealt.filter((t) => t === tile).length < copies;
}
// ---------------------------------------------------------------------------
// Locking answers
// ---------------------------------------------------------------------------
function submissionBucket(player) {
    return player.role === 'spectator' ? 'spectatorSubmissions' : 'submissions';
}
function existingSubmission(round, player) {
    return round[submissionBucket(player)][player.id];
}
function storeSubmission(draft, round, player, submission, 
/**
 * Whether this counts as the player declaring themselves ready.
 *
 * False for an offered word under open answering. An offer is not a lock: it
 * must not tell the room the player is done, and it must not count towards
 * `everyoneLocked`, which ends the round the moment everybody is. Announcing
 * it did both, so a room where everyone offered one word had the clock cut
 * out from under it — the exact opposite of what open answering is for.
 */
announceLock = true) {
    const bucket = submissionBucket(player);
    const next = { ...round[bucket], [player.id]: submission };
    draft.state = replaceRound(draft.state, patchRound(round, bucket === 'submissions' ? { submissions: next } : { spectatorSubmissions: next }));
    if (announceLock && player.role === 'player') {
        draft.events.push({ type: 'player-locked', playerId: player.id });
    }
}
/** Shared guards for every deliberate lock. */
/**
 * How far into a round's clock an answer landed, pauses excluded.
 *
 * A thin wrapper so every submission path spells it the same way, and so the
 * null case — a round with no clock — is written once. Never
 * `lockedAt - round.startedAt`: see the note on `elapsedMs` in `clock.ts`.
 */
function intoRound(clock, at) {
    return clock === null ? null : elapsedMs(clock, at);
}
function lockGuards(state, playerId, phase, clientLockedAt) {
    if (state.phase !== phase)
        return { ok: false, result: fail('not the right phase', 'invalid-state') };
    const round = currentRound(state);
    if (round === null || round.revealedAt !== null) {
        return { ok: false, result: fail('this round is closed', 'invalid-state') };
    }
    const player = state.players[playerId];
    if (player === undefined)
        return { ok: false, result: fail('no such player', 'invalid-action') };
    if (player.role === 'spectator' && !state.settings.allowSpectatorPlayAlong) {
        return { ok: false, result: fail('spectator play-along is off', 'invalid-action') };
    }
    if (round.clock === null) {
        return { ok: false, result: fail('the round clock is missing', 'invalid-state') };
    }
    // A locked answer can be changed while the clock is still running, which is
    // what the "Unlock and edit" the design draws actually does. Refusing a second
    // lock made that button a lie: the player edited their word, pressed it, and
    // the server kept the first answer without saying so.
    //
    // Nothing is given away by allowing it. Opponents see a lock indicator and
    // never a length, so flipping between locked and thinking leaks nothing, and
    // the deadline check below is what decides whether an answer counts at all.
    // The show works the same way: a contestant may change their word until time
    // is called.
    //
    // The conundrum does NOT come through here. One answer each is the rule there,
    // and a wrong one ends that player's round.
    //
    // A lock at t=29.8 on a 400ms connection arrives late and is accepted. A
    // player who dropped at t=5 can only claim up to about t=6.5.
    if (!acceptsAnswerAt(player, round.clock, clientLockedAt)) {
        return { ok: false, result: fail('that lock arrived too late to count', 'invalid-action') };
    }
    return { ok: true, player, round };
}
function lockLetters(state, playerId, word, clientLockedAt, reasoning = null, allowUnbuildable = false) {
    const guard = lockGuards(state, playerId, 'letters-play', clientLockedAt);
    if (!guard.ok)
        return guard.result;
    const { player, round } = guard;
    if (round.type !== 'letters')
        return fail('no letters round', 'invalid-state');
    const normalized = normalizeWord(word);
    if (!allowUnbuildable &&
        normalized.length > 0 &&
        !canBuildFromTiles(normalized, round.tiles.map((tile) => tile.letter))) {
        return fail('that word is not buildable from these tiles', 'invalid-action');
    }
    const draft = draftOf(state);
    const previous = existingSubmission(round, player);
    storeSubmission(draft, round, player, {
        kind: 'letters',
        playerId,
        word: normalized,
        declaredLength: normalized.length === 0 ? null : normalized.length,
        lockedAt: clientLockedAt,
        firstKeystrokeAt: previous?.firstKeystrokeAt ?? null,
        msIntoRound: intoRound(round.clock, clientLockedAt),
        valid: null,
        score: null,
        judgedValid: null,
        hostOverride: null,
        actionId: null,
        fromDraft: false,
        reasoning,
    });
    return done(draft);
}
/**
 * One offered word, while `lettersWordCheck` is on.
 *
 * The rule is one-directional in score: a player's stored answer never gets
 * shorter. An invalid word, or a valid one shorter than the one already held,
 * is accepted as an action and changes nothing — the server still answers its
 * author, and that answer is the whole point of offering it. A valid word of
 * the same length as the one held DOES replace it: see below.
 *
 * Two things this deliberately does NOT do.
 *
 * It does not touch the lock. A player may be locked in and carry on trying
 * words; a longer one is adopted underneath them without costing them their
 * ready state, which is the entire reason the setting exists.
 *
 * It does not record the attempt. There is no list of everything somebody tried
 * anywhere in `ServerGameState`, and there must not be one: a list of a
 * player's thinking is exactly the kind of thing a sanitization bug hands to
 * the whole room, which is why drafts live outside the state as well.
 */
function submitWord(state, playerId, word, valid, score, clientSubmittedAt) {
    if (!state.settings.lettersWordCheck) {
        return fail('open answering is off in this room', 'invalid-action');
    }
    // The same guards every deliberate answer goes through. Offering a word is
    // answering, so it is subject to the deadline — an offer must not be able to
    // land in the gap between the clock expiring and the judging tick — and to
    // the room's spectator play-along setting. Checking only the phase, as this
    // did, left both holes open.
    const guard = lockGuards(state, playerId, 'letters-play', clientSubmittedAt);
    if (!guard.ok)
        return guard.result;
    const { player, round } = guard;
    if (round.type !== 'letters')
        return fail('no letters round', 'invalid-state');
    const normalized = normalizeWord(word);
    if (normalized.length === 0)
        return fail('there is no word there', 'invalid-action');
    if (!canBuildFromTiles(normalized, round.tiles.map((tile) => tile.letter))) {
        return fail('that word is not buildable from these tiles', 'invalid-action');
    }
    const draft = draftOf(state);
    const previous = existingSubmission(round, player);
    // A numbers submission cannot be sitting here — the phase guard above rules
    // that out — but the union does not know it, and narrowing is cheaper than a
    // cast that would go stale the day a third answer kind appears.
    const held = previous !== undefined && previous.kind === 'letters' ? previous.word : '';
    // Ties go to the LATEST word: offering MINT and then TINT leaves you on TINT.
    // The old rule kept the earlier one, on the reasoning that retyping at the
    // same length should race nobody. It read as the room ignoring you — you
    // offer a word, it comes back valid, and the answer on the reveal is the
    // other one. A player who types a second seven has changed their mind about
    // which seven they want, and there is nothing to weigh up: both score the
    // same, so the tie is free to go to whichever one they said last.
    if (!valid || normalized.length < held.length)
        return done(draft);
    storeSubmission(draft, round, player, {
        kind: 'letters',
        playerId,
        word: normalized,
        declaredLength: normalized.length,
        // Null unless they really did lock. An offer is not a declaration of
        // being ready: `everyoneLocked` ends the round the instant every seated
        // player has a non-null `lockedAt`, so stamping one here ended the round
        // as soon as everybody had offered a single word. A player who locked
        // first keeps their own lock time — finding a better word afterwards is
        // the point of the setting and must not re-time them.
        lockedAt: previous?.lockedAt ?? null,
        firstKeystrokeAt: previous?.firstKeystrokeAt ?? null,
        // Follows `lockedAt`, for the same reason: an offer is not a lock, and a
        // player who locked first must not be re-timed by finding a better word.
        msIntoRound: previous?.msIntoRound ?? null,
        // The server has already ruled on it, and judging re-derives both anyway.
        valid: true,
        score,
        judgedValid: true,
        hostOverride: null,
        actionId: null,
        fromDraft: false,
        // Open answering is a human affordance: an LLM bot answers once, through
        // `lock-letters`, and never arrives here.
        reasoning: null,
    }, 
    // Never announces a lock: see above.
    false);
    return done(draft);
}
/**
 * Bank every value a numbers step list passes through, for one player.
 *
 * **Cumulative for the life of the round, and nothing takes anything out.**
 * That is the whole feature: `unlock` deletes a submission, `Start over` sends
 * an empty draft, a second lock replaces the first, and none of them may cost a
 * player a value they had already made. See `NumbersRound.bests` and DESIGN
 * §6.4.
 *
 * Called on every path the engine learns a step list on — a deliberate lock and
 * an adopted draft — rather than only on the lock, because the lock is the one
 * a player fiddling with their board has not pressed yet.
 *
 * **NOT FOR BOTS, and this is the one line to change if that is wrong.** A bot
 * does not experiment: it computes one answer and plays it, and the values
 * inside its step list are the shape of the solver's search rather than values
 * a seat ever reached and thought about. Banking them re-tunes every difficulty
 * below hard, invisibly, because a weak bot's answer is chosen by percentile
 * over answer quality (DESIGN §6.7) and the witness for a deliberately mediocre
 * value very often passes straight through a good one. Measured over five
 * boards and every value within 60 of the target: 7.1% of witnesses would be
 * promoted, worth 5.7 points each when they were — concentrated on exactly the
 * weak answers the ladder is calibrated on. `bots.test.ts` pins the ladder by
 * the answer a bot CHOOSES, so it would not have caught it.
 *
 * Pure: it reads the round it is handed and the arithmetic, and no clock.
 */
function withNumbersBest(round, player, steps) {
    if (round.type !== 'numbers' || round.target === null)
        return round;
    if (player.isBot === true)
        return round;
    const held = round.bests?.[player.id] ?? null;
    const next = foldNumbersBest(held, round.tiles, steps, round.target);
    // `foldNumbersBest` hands `held` straight back when nothing beat it, so this
    // is an identity check rather than a deep one.
    if (next === null || next === held)
        return round;
    return { ...round, bests: { ...(round.bests ?? {}), [player.id]: next } };
}
function lockNumbers(state, playerId, steps, clientLockedAt, reasoning = null) {
    const guard = lockGuards(state, playerId, 'numbers-play', clientLockedAt);
    if (!guard.ok)
        return guard.result;
    const { player, round } = guard;
    if (round.type !== 'numbers')
        return fail('no numbers round', 'invalid-state');
    let value = null;
    if (steps.length > 0) {
        const checked = validateStepList(round.tiles, steps);
        if (!checked.ok)
            return fail(`step ${checked.stepIndex + 1}: ${checked.error}`, 'illegal-step');
        value = checked.value;
    }
    const draft = draftOf(state);
    // Banked BEFORE the submission is stored, and the banked round is what the
    // submission is stored onto — `storeSubmission` patches the round object it is
    // handed, so patching the pre-bank one would drop the bank on the floor.
    const banked = withNumbersBest(round, player, steps);
    if (banked !== round)
        draft.state = replaceRound(draft.state, banked);
    const previous = existingSubmission(banked, player);
    storeSubmission(draft, banked, player, {
        kind: 'numbers',
        playerId,
        steps: steps.map((step) => ({ ...step })),
        value,
        declaredLength: value,
        lockedAt: clientLockedAt,
        firstKeystrokeAt: previous?.firstKeystrokeAt ?? null,
        msIntoRound: intoRound(round.clock, clientLockedAt),
        valid: null,
        score: null,
        judgedValid: null,
        hostOverride: null,
        actionId: null,
        fromDraft: false,
        reasoning,
    });
    return done(draft);
}
/**
 * Drop a player's answer, and the lock indicator everyone else can see.
 *
 * A lock is a ready signal rather than a commitment: what gets submitted is
 * always the last thing the player typed. So this is reached two ways — the
 * player presses "unlock and edit", or a draft arrives from somebody who is
 * still showing as locked, which means they are evidently not done. Both delete
 * the stored answer. Whatever they type next is adopted when the round is
 * judged, and a deliberate lock replaces it outright.
 *
 * Doing it as a real action rather than in the client is the point: unlocking
 * used to be local state, so the player editing their word carried on reading as
 * locked to everybody else, and "everyone is locked" could never be trusted.
 *
 * The conundrum deliberately does not come through here. One answer each is the
 * rule, a wrong one locks that player out for the round, and there is nothing
 * left to take back.
 */
function unlock(state, playerId) {
    const round = currentRound(state);
    if (round === null || round.revealedAt !== null) {
        return fail('this round is closed', 'invalid-state');
    }
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (round.type === 'conundrum')
        return fail('a conundrum answer is final', 'invalid-action');
    const bucket = submissionBucket(player);
    if (round[bucket][playerId] === undefined) {
        // Already unlocked. A retry after a reconnect is not an error.
        return { ok: true, state, events: [] };
    }
    const draft = draftOf(state);
    const remaining = { ...round[bucket] };
    delete remaining[playerId];
    draft.state = replaceRound(draft.state, patchRound(round, bucket === 'submissions'
        ? { submissions: remaining }
        : { spectatorSubmissions: remaining }));
    if (player.role === 'player') {
        draft.events.push({ type: 'player-unlocked', playerId });
    }
    return done(draft);
}
function adoptDraft(state, now, playerId, input) {
    const round = currentRound(state);
    if (round === null || round.revealedAt !== null) {
        return fail('this round is closed', 'invalid-state');
    }
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    const previous = existingSubmission(round, player);
    if (previous !== undefined && !previous.fromDraft) {
        return fail('that player already locked in', 'invalid-action');
    }
    const draft = draftOf(state);
    if (input.kind === 'letters') {
        if (round.type !== 'letters')
            return fail('not a letters round', 'invalid-state');
        const normalized = normalizeWord(input.word);
        // The same check `lock-letters` makes, and for the same reason. Without it a
        // draft was a way round the board entirely: the client streams any word it
        // likes, never locks, and the adopt at time-up stores it — and the backend
        // judges letters on the dictionary alone, so it scored. Numbers never had
        // the hole because the adopt below runs `validateStepList`.
        //
        // Refusing is what "scores nothing" looks like here: the only caller
        // dispatches this quietly in a loop at judging time and ignores failures, so
        // a player who streamed something impossible simply ends with no answer.
        if (normalized.length > 0 &&
            !canBuildFromTiles(normalized, round.tiles.map((tile) => tile.letter))) {
            return fail('that word is not buildable from these tiles', 'invalid-action');
        }
        storeSubmission(draft, round, player, {
            kind: 'letters',
            playerId,
            word: normalized,
            declaredLength: normalized.length === 0 ? null : normalized.length,
            lockedAt: now,
            firstKeystrokeAt: previous?.firstKeystrokeAt ?? null,
            msIntoRound: intoRound(round.clock, now),
            valid: null,
            score: null,
            judgedValid: null,
            hostOverride: null,
            actionId: null,
            fromDraft: true,
            // A draft is streamed from a client keystroke by keystroke. Bots do not
            // stream, so nothing that lands here ever has reasoning attached.
            reasoning: null,
        });
        return done(draft);
    }
    if (round.type !== 'numbers')
        return fail('not a numbers round', 'invalid-state');
    let value = null;
    if (input.steps.length > 0) {
        const checked = validateStepList(round.tiles, input.steps);
        // An illegal streamed draft scores nothing rather than rejecting the adopt.
        // A disconnected player is not around to fix it.
        value = checked.ok ? checked.value : null;
    }
    // The same bank the lock makes, and the reason this path needs it too is that
    // a player who never locked is exactly the player this feature is for. An
    // illegal tail does not stop it: `foldNumbersBest` banks the legal prefix.
    const banked = withNumbersBest(round, player, input.steps);
    if (banked !== round)
        draft.state = replaceRound(draft.state, banked);
    storeSubmission(draft, banked, player, {
        kind: 'numbers',
        playerId,
        steps: input.steps.map((step) => ({ ...step })),
        value,
        declaredLength: value,
        lockedAt: now,
        firstKeystrokeAt: previous?.firstKeystrokeAt ?? null,
        msIntoRound: intoRound(round.clock, now),
        valid: null,
        score: null,
        judgedValid: null,
        hostOverride: null,
        actionId: null,
        fromDraft: true,
        reasoning: null,
    });
    return done(draft);
}
// ---------------------------------------------------------------------------
// Conundrum
// ---------------------------------------------------------------------------
/**
 * Everyone eligible is locked out, so the round has nothing left to give.
 *
 * Never true with the buzzer off, and it does not need a clause saying so:
 * nothing locks anybody out there, so `lockedOut` stays empty and every seat
 * stays eligible. The round runs its clock out, which is what "everybody who has
 * it right when the clock stops" means — see `GameSettings.conundrumBuzzer`.
 */
function conundrumExhausted(round, state) {
    const eligible = seatedIds(state).filter((id) => !round.lockedOut.includes(id));
    return eligible.length === 0;
}
function buzz(state, now, playerId, compensatedAt) {
    if (state.phase !== 'conundrum-play')
        return fail('not a conundrum', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'conundrum')
        return fail('no conundrum round', 'invalid-state');
    // Refused plainly rather than by accident. With the buzzer off there is no
    // race to enter: anybody may answer at any point inside the clock, and a buzz
    // that silently did nothing would leave a client waiting for a window that is
    // never going to open.
    if (conundrumBuzzerOff(round)) {
        return fail('this room plays the conundrum without a buzzer', 'invalid-action');
    }
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.role !== 'player')
        return fail('spectators do not buzz', 'invalid-action');
    if (round.solvedBy !== null)
        return fail('this conundrum is solved', 'invalid-state');
    if (round.buzzedBy !== null)
        return fail('someone else is buzzed in', 'invalid-action');
    if (round.lockedOut.includes(playerId)) {
        return fail('you are locked out of this conundrum', 'invalid-action');
    }
    if (round.clock === null)
        return fail('the round clock is missing', 'invalid-state');
    if (!acceptsAnswerAt(player, round.clock, compensatedAt)) {
        return fail('that buzz arrived too late to count', 'invalid-action');
    }
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, {
        ...round,
        buzzedBy: playerId,
        buzzClock: startClock(draft.state.settings.conundrumBuzzAnswerSeconds * 1000, now),
        // A buzz pauses the main clock. The rest of the room resumes with whatever
        // was left, which is why the pause has to be real rather than cosmetic.
        clock: pauseClock(round.clock, now),
    });
    draft.events.push({ type: 'buzz-accepted', playerId, at: compensatedAt });
    return done(draft);
}
/** A wrong or unanswered buzz: lock the player out and give the room its clock back. */
function failBuzz(draft, round, playerId, answer, now, 
/** Kept on a wrong answer too: how a model talked itself into it is the interesting half. */
reasoning = null) {
    const player = draft.state.players[playerId];
    const submissions = {
        ...round.submissions,
        [playerId]: {
            kind: 'conundrum',
            playerId,
            answer,
            declaredLength: answer.trim().length === 0 ? null : answer.trim().length,
            lockedAt: answer.trim().length === 0 ? null : now,
            firstKeystrokeAt: round.submissions[playerId]?.firstKeystrokeAt ?? null,
            // The main clock is paused where the buzz left it, so this is time to the
            // BUZZ rather than to the answer — which is the number worth having.
            msIntoRound: intoRound(round.clock, now),
            valid: false,
            score: 0,
            judgedValid: false,
            hostOverride: round.submissions[playerId]?.hostOverride ?? null,
            actionId: null,
            fromDraft: false,
            reasoning,
        },
    };
    const next = {
        ...round,
        submissions,
        lockedOut: round.lockedOut.includes(playerId)
            ? round.lockedOut
            : [...round.lockedOut, playerId],
        buzzedBy: null,
        buzzClock: null,
        clock: round.clock === null ? null : resumeClock(round.clock, now),
    };
    draft.state = replaceRound(draft.state, next);
    draft.events.push({ type: 'buzz-result', playerId, correct: false, answer });
    if (player !== undefined) {
        pushLog(draft, 'round-ended', `${player.displayName} answered wrong and is locked out`, now);
    }
}
/**
 * File an AI opponent's conundrum answer against the round it belonged to.
 *
 * **Targets a round index, and the round is usually finished.** A model may take
 * two minutes, and the request is no longer cancelled when the round ends, so
 * this arrives late by design — three rounds later, or after the last one. The
 * index is what keeps it attached to the conundrum it is an answer to.
 *
 * Nothing here is scoring. It writes one map on one round and touches no
 * submission, no clock, no standing and no phase, which is why it is allowed to
 * act on a round the game has moved past — an action that could change a
 * finished round's OUTCOME would not be.
 *
 * Idempotent per seat: a seat that already has a shadow keeps the first one. Two
 * arrivals for one seat means something retried, and the second is not a second
 * attempt at the conundrum.
 */
function recordShadowAnswer(state, roundIndex, shadow) {
    const round = state.rounds[roundIndex];
    if (round === undefined || round.type !== 'conundrum') {
        return fail('no conundrum at that round', 'invalid-state');
    }
    if (state.players[shadow.playerId] === undefined) {
        // The seat left. Nothing to attach it to, and a shadow for a player who is
        // not in the room would be an answer from nobody.
        return fail('no such player', 'invalid-action');
    }
    const existing = round.shadowAnswers ?? {};
    if (existing[shadow.playerId] !== undefined)
        return done(draftOf(state));
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, { ...round, shadowAnswers: { ...existing, [shadow.playerId]: shadow } });
    return done(draft);
}
function submitConundrum(state, now, playerId, answer, correct, clientLockedAt, reasoning = null) {
    if (state.phase !== 'conundrum-play')
        return fail('not a conundrum', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.type !== 'conundrum')
        return fail('no conundrum round', 'invalid-state');
    const player = state.players[playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    // **The no-buzzer room, and it is a different round rather than the same one
    // with a control removed.** Anybody may answer at any point inside the round
    // clock, an answer locks nobody out, and everybody who has it right when the
    // clock stops scores. Nothing here reads or writes `buzzedBy`, `buzzClock` or
    // `lockedOut`, and nothing ranks by arrival — see `GameSettings.conundrumBuzzer`
    // for why a fastest-tenth scheme was considered and dropped.
    if (conundrumBuzzerOff(round)) {
        return submitOpenConundrum(state, player, answer, correct, clientLockedAt, reasoning);
    }
    if (round.buzzedBy !== playerId)
        return fail('you are not buzzed in', 'not-your-turn');
    if (round.buzzClock === null)
        return fail('the answer window is missing', 'invalid-state');
    if (!acceptsAnswerAt(player, round.buzzClock, clientLockedAt)) {
        return fail('that answer arrived too late to count', 'invalid-action');
    }
    const draft = draftOf(state);
    const normalized = normalizeWord(answer);
    if (!correct) {
        failBuzz(draft, round, playerId, normalized, now, reasoning);
        return done(draft);
    }
    const submissions = {
        ...round.submissions,
        [playerId]: {
            kind: 'conundrum',
            playerId,
            answer: normalized,
            declaredLength: normalized.length === 0 ? null : normalized.length,
            lockedAt: clientLockedAt,
            firstKeystrokeAt: round.submissions[playerId]?.firstKeystrokeAt ?? null,
            // Paused at the buzz, so this is time to the buzz. See `failBuzz`.
            msIntoRound: intoRound(round.clock, clientLockedAt),
            valid: true,
            score: CONUNDRUM_POINTS,
            judgedValid: true,
            hostOverride: null,
            actionId: null,
            fromDraft: false,
            reasoning,
        },
    };
    // The main clock stays paused where the buzz left it: the round is over, and
    // freezing it there is what a restart mid-reveal has to reproduce.
    draft.state = replaceRound(draft.state, {
        ...round,
        submissions,
        solvedBy: playerId,
        buzzedBy: null,
        buzzClock: null,
    });
    draft.state = recomputeScores(draft.state);
    draft.events.push({ type: 'buzz-result', playerId, correct: true, answer: normalized });
    draft.events.push({ type: 'player-locked', playerId });
    pushLog(draft, 'round-ended', `${player.displayName} solved the conundrum`, now);
    return done(draft);
}
/**
 * A conundrum answer in a room with no buzzer.
 *
 * **Nothing here is a race and nothing here is final.** A wrong answer costs
 * nothing but the try, so a player may keep going until the clock stops; a right
 * one is kept and further answers from that player are success with no effect,
 * because somebody who has already solved it cannot mean to unsolve it and a
 * stray keystroke must not be able to. `solvedBy` is never set — there is no
 * solver, there are however many people got it — which is also what keeps
 * `effectsFor` running the round to its clock rather than ending it on the first
 * correct answer.
 *
 * Spectators may play along here, unlike in a buzzered room, where they cannot
 * because `buzz` refuses them and the answer window is the only door. Their
 * answers land in `spectatorSubmissions` as they do on every other round type,
 * so nobody else ever sees one and the audience seat can fold them.
 *
 * The score is written straight onto the submission for the same reason the
 * buzzered path does it: judging re-derives it from the round anyway, and a
 * correct answer that showed nothing until the reveal would be the one round
 * type where a player could not tell whether they had it.
 */
function submitOpenConundrum(state, player, answer, correct, clientLockedAt, reasoning) {
    const round = currentRound(state);
    if (round === null || round.type !== 'conundrum')
        return fail('no conundrum round', 'invalid-state');
    if (round.revealedAt !== null)
        return fail('this round is closed', 'invalid-state');
    if (round.clock === null)
        return fail('the round clock is missing', 'invalid-state');
    if (player.role === 'spectator' && !state.settings.allowSpectatorPlayAlong) {
        return fail('spectator play-along is off', 'invalid-action');
    }
    if (!acceptsAnswerAt(player, round.clock, clientLockedAt)) {
        return fail('that answer arrived too late to count', 'invalid-action');
    }
    const held = existingSubmission(round, player);
    if (held !== undefined && held.valid === true) {
        // Already right. A retry after a reconnect lands here, and so does somebody
        // typing on: neither is a reason to take a correct answer away.
        return { ok: true, state, events: [] };
    }
    const draft = draftOf(state);
    const normalized = normalizeWord(answer);
    storeSubmission(draft, round, player, {
        kind: 'conundrum',
        playerId: player.id,
        answer: normalized,
        declaredLength: normalized.length === 0 ? null : normalized.length,
        lockedAt: clientLockedAt,
        firstKeystrokeAt: held?.firstKeystrokeAt ?? null,
        msIntoRound: intoRound(round.clock, clientLockedAt),
        valid: correct,
        score: correct ? CONUNDRUM_POINTS : 0,
        judgedValid: correct,
        hostOverride: null,
        actionId: null,
        fromDraft: false,
        reasoning,
    }, 
    // Only a right answer says somebody is done. A wrong one can be tried again
    // until the clock stops, so announcing it would light the lock indicator for
    // a player who is still typing — and in a room of two hundred it is one
    // broadcast per guess for a fact that is not true yet.
    correct);
    if (correct)
        draft.state = recomputeScores(draft.state);
    draft.events.push({ type: 'buzz-result', playerId: player.id, correct, answer: normalized });
    return done(draft);
}
// ---------------------------------------------------------------------------
// Judging and the reveal
// ---------------------------------------------------------------------------
function recomputeScores(state) {
    const totals = {};
    for (const round of state.rounds) {
        for (const [playerId, score] of Object.entries(scoreRound(round))) {
            totals[playerId] = (totals[playerId] ?? 0) + score;
        }
    }
    const players = { ...state.players };
    for (const id of Object.keys(players)) {
        const score = totals[id] ?? 0;
        if (players[id].score !== score)
            players[id] = { ...players[id], score };
    }
    return { ...state, players };
}
function judgedSubmission(submission, judgment) {
    // What the dictionary and the arithmetic decided, kept whether or not a ruling
    // is sitting on top of it. This is what an undo restores, and it is the only
    // copy: `valid` below is overwritten by the ruling.
    const judgedValid = judgment !== undefined ? judgment.valid : submission.judgedValid ?? submission.valid;
    // The host has the last word in both directions, so an override already on the
    // record survives the dictionary's opinion.
    const valid = submission.hostOverride !== null ? submission.hostOverride === 'allow' : judgedValid;
    switch (submission.kind) {
        case 'letters':
            return { ...submission, valid, judgedValid, word: judgment?.normalized ?? submission.word };
        case 'numbers':
            return { ...submission, valid, judgedValid, value: judgment?.value ?? submission.value };
        case 'conundrum':
            return { ...submission, valid, judgedValid };
    }
}
function scoreSpectator(round, submission) {
    const valid = submission.hostOverride === 'allow' || submission.valid === true;
    switch (submission.kind) {
        case 'letters':
            return scoreLettersWord(submission.word, valid);
        case 'numbers':
            return round.type === 'numbers' && round.target !== null
                ? scoreNumbersValue(valid ? submission.value : null, round.target)
                : 0;
        case 'conundrum':
            // A play-along conundrum answer is worth nothing in a buzzered room,
            // because a spectator cannot buzz and so cannot be first. With no buzzer
            // there is no first, and a spectator who solved it should be shown the ten
            // points their own answer was worth — it is what the audience seat folds,
            // and a play-along that reported zero for a right answer would be lying to
            // the one person it is for.
            return scoreConundrum(valid, conundrumBuzzerOff(round));
    }
}
function clientSubmissionOf(submission) {
    const base = {
        playerId: submission.playerId,
        locked: submission.lockedAt !== null || submission.fromDraft,
        declaredLength: submission.declaredLength,
        valid: submission.valid,
        score: submission.score,
        // What the dictionary said, beside what is in force. The reveal renders a
        // host ruling as an overrule of a verdict, which needs both — and needs the
        // word as submitted, which the per-kind branches below carry.
        judgedValid: submission.judgedValid ?? null,
        hostOverride: submission.hostOverride,
        // Safe here and nowhere else: every caller of this function has already
        // decided the answer is public, either because the round is revealed or
        // because it is going back to its own author. The pre-reveal path is
        // `hiddenSubmissionOf`, which does not carry this field at all.
        reasoning: submission.reasoning,
    };
    switch (submission.kind) {
        case 'letters':
            return { ...base, word: submission.word };
        case 'numbers':
            return { ...base, steps: submission.steps, value: submission.value };
        case 'conundrum':
            return { ...base, answer: submission.answer };
    }
}
// ---------------------------------------------------------------------------
// Reveal pacing
//
// The reveal is host-paced: one player at a time, in `revealOrder`, and the next
// round only arrives when the host says so or forty-five seconds after the last
// answer is out. The pacing lives here rather than in the client because every
// client has to see the same answer at the same moment, and because a client
// animating on its own timer is exactly how the reveal ended up racing past
// faster than anyone could read it.
//
// `round.clock` stays the clock for the current phase, as it is everywhere else:
// during a reveal it holds the wait for the next entry, then the wait for the
// auto-advance. That is what makes the reveal pause with the room and come back
// correctly from a restart. `nextRevealAt` and `revealAdvanceAt` are the
// readable copies, re-derived from the clock every time it moves.
// ---------------------------------------------------------------------------
/**
 * How long the entry at `index` holds the screen.
 *
 * An actual answer needs longer to read than a dash, and where the entry sits in
 * the queue decides how much of that it actually gets: the curve is
 * `revealHoldMs` in `constants.ts`, with the reasoning for its shape. This
 * function's only job is to say which entry it is and whether there is anything
 * in it, and to hand the room's own pace through.
 */
function revealEntryMs(round, order, index, settings) {
    const playerId = order[index];
    const submission = playerId === undefined ? undefined : round.submissions[playerId];
    const empty = submission === undefined ||
        submission.declaredLength === null ||
        (submission.kind === 'letters' && submission.word.trim().length === 0) ||
        (submission.kind === 'numbers' && submission.value === null) ||
        (submission.kind === 'conundrum' && submission.answer.trim().length === 0);
    return revealHoldMs({
        index,
        count: order.length,
        hasAnswer: !empty,
        pacePercent: settings.revealPacePercent,
    });
}
function revealCursorOf(round) {
    return typeof round.revealCursor === 'number' ? round.revealCursor : 0;
}
/**
 * How many steps the reveal has, which is one more than it has answers.
 *
 * **The extra step is the last answer's own hold, and it is a fix rather than
 * an ornament.** Releasing the final entry and the cursor reaching the end used
 * to happen on the same tick, so the reveal completed in the frame the last
 * answer landed in and the client's payoff replaced it a beat later. Every
 * other entry held for two and a half to six seconds; the last one held for
 * whatever the client's payoff beat was. The queue is worst-first, so the entry
 * being cut short was the best answer in the round, every round — which is what
 * "too fast on the 2nd reveal" was, in a two-player game where the second entry
 * is the last one.
 *
 * A round nobody answered has no steps at all, and is finished the moment it is
 * judged. There is nothing to hold on.
 *
 * **A conundrum has no steps either, whatever its queue says.** Its queue holds
 * every player who buzzed, but the reveal draws one thing — the solver's answer,
 * or nothing if the round went unsolved (RevealScreen collapsed it, because there
 * is only ever one right answer and everyone has been staring at it). Pacing a
 * queue of failed buzzes therefore held a bare frame for two and a half to six
 * seconds each, with nothing on screen changing, before the solution was
 * released. The whole of a conundrum reveal is available the instant it is
 * judged, so it is complete the instant it is judged, and the only clock it ever
 * runs is the auto-advance.
 */
function revealStepCount(round) {
    if (round.type === 'conundrum')
        return 0;
    const answers = revealOrder(round).length;
    return answers === 0 ? 0 : answers + 1;
}
function isRevealComplete(round) {
    return round.revealedAt !== null && revealCursorOf(round) >= revealStepCount(round);
}
/**
 * Put the reveal on a clock: the wait for entry number `cursor`, or the
 * auto-advance window once every entry is out and the last one has had its beat.
 * One clock, so pause, resume and `nextTickAt` all keep working without knowing
 * the reveal exists.
 *
 * At `cursor === order.length` — the trailing step — `order[cursor - 1]` is the
 * last answer, so it gets exactly the hold every other entry got. That is the
 * whole of the trailing step: it releases nothing and it holds one thing.
 */
function pacedReveal(round, now, settings) {
    const order = revealOrder(round);
    const cursor = revealCursorOf(round);
    if (cursor >= revealStepCount(round)) {
        // The one place the auto-advance window is started, and therefore the one
        // place a hold armed mid-reveal can land. See `RoundBase.revealHoldAtEnd`:
        // the host asked for the round to finish paying off and THEN wait, so the
        // pause is applied to the window at the instant the window opens.
        //
        // `revealAdvanceAt` is still written, and it is still the honest reading:
        // `syncRevealDeadlines` recomputes it from the clock's effective deadline
        // when the hold is lifted, and until then the screen shows `held` rather
        // than a number counting toward it.
        const started = startClock(REVEAL_AUTO_ADVANCE_MS, now);
        return patchRound(round, {
            clock: round.revealHoldAtEnd === true ? pauseClock(started, now) : started,
            nextRevealAt: null,
            revealAdvanceAt: now + REVEAL_AUTO_ADVANCE_MS,
        });
    }
    const waitMs = cursor === 0
        ? revealLeadMs(settings.revealPacePercent)
        : revealEntryMs(round, order, cursor - 1, settings);
    return patchRound(round, {
        clock: startClock(waitMs, now),
        nextRevealAt: now + waitMs,
        revealAdvanceAt: null,
    });
}
/**
 * Release the next entry, or the whole rest of them when the host says go.
 *
 * Returns the round with its cursor moved and its clock re-armed, plus the ids
 * released, so the caller can emit one `reveal-step` per answer at the moment it
 * actually becomes visible.
 *
 * The cursor may land on the trailing step, which names no player: `released` is
 * sliced against the queue rather than against the cursor, so that step reports
 * nothing released and emits no `reveal-step`.
 */
function releaseReveal(round, now, count, settings) {
    const order = revealOrder(round);
    const cursor = revealCursorOf(round);
    const next = Math.min(revealStepCount(round), cursor + count);
    const moved = patchRound(round, { revealCursor: next });
    return {
        round: pacedReveal(moved, now, settings),
        released: order.slice(Math.min(cursor, order.length), Math.min(next, order.length)),
    };
}
function revealPhaseFor(type) {
    switch (type) {
        case 'letters':
            return 'letters-reveal';
        case 'numbers':
            return 'numbers-reveal';
        case 'conundrum':
            return 'conundrum-reveal';
    }
}
/**
 * A submission carrying the best value its author reached, rather than the one
 * that happened to be on their board.
 *
 * **THE RULE CHANGE, DESIGN §6.4, 2026-08-03.** On the show a contestant
 * declares an answer and that is the answer. Here they do not: the board is a
 * scratchpad, the round is 45 seconds of arithmetic by thumb, and the thing
 * that kept happening is a player reaching 175 exactly, carrying on, and
 * submitting the wreck. So the round pays what they reached. Isaac chose this
 * knowing it departs from the programme.
 *
 * It rewrites the submission rather than passing a second number to the scorer,
 * and that is deliberate: `scoreRound`, `revealOrder`'s distance sort, the
 * reveal slide and the game record all read the submission, and any of them
 * left reading the wreck would contradict the score beside it.
 *
 * **Strictly closer only.** A best that merely ties the board on distance
 * scores the same, so the player's own last declaration stands — the fewer-steps
 * tie-break decides which best is HELD, not whether to overrule a player who
 * said something worth the same.
 */
function promotedToBest(submission, best, playerId, target) {
    if (submission !== undefined && submission.kind !== 'numbers')
        return null;
    if (submission !== undefined && submission.value !== null) {
        if (best.distance >= Math.abs(submission.value - target))
            return null;
    }
    // A value the engine folded out of a step list it validated itself, so it is
    // valid by construction. A host ruling already on the record still has the
    // last word, exactly as it does in `judgedSubmission`.
    const hostOverride = submission?.hostOverride ?? null;
    const valid = hostOverride !== null ? hostOverride === 'allow' : true;
    return {
        kind: 'numbers',
        playerId,
        steps: best.steps.map((step) => ({ ...step })),
        value: best.value,
        declaredLength: best.value,
        // Nothing is invented about WHEN. A best reached and then abandoned was
        // never locked in, and stamping a lock time here would put a fiction into
        // `ms_into_round` and into the "who answered first" ordering.
        lockedAt: submission?.lockedAt ?? null,
        firstKeystrokeAt: submission?.firstKeystrokeAt ?? null,
        msIntoRound: submission?.msIntoRound ?? null,
        valid,
        score: null,
        judgedValid: true,
        hostOverride,
        actionId: submission?.actionId ?? null,
        // Not a deliberate lock. It is the same claim `adopt-draft` makes about a
        // streamed answer, and it is the honest one.
        fromDraft: true,
        reasoning: submission?.reasoning ?? null,
    };
}
/**
 * Apply the best each player reached to the answers about to be scored.
 *
 * Runs over `round.bests`, not over the submissions, because the player this
 * exists for may have no submission at all: locking 175, pressing `Start over`
 * and never locking again leaves the engine holding their 175 and the round
 * holding nothing of theirs.
 */
function withPromotedBests(state, round, submissions, spectatorSubmissions) {
    if (round.type !== 'numbers' || round.target === null || round.bests === undefined) {
        return { submissions, spectatorSubmissions };
    }
    const promoted = { ...submissions };
    const promotedSpectators = { ...spectatorSubmissions };
    for (const [playerId, best] of Object.entries(round.bests)) {
        // Which bucket this answer belongs in. An answer already filed says so
        // itself; otherwise the seat does, and a player who has left the room since
        // has neither and is skipped — there is nobody left to score.
        const bucket = submissions[playerId] !== undefined
            ? 'submissions'
            : spectatorSubmissions[playerId] !== undefined
                ? 'spectatorSubmissions'
                : state.players[playerId] === undefined
                    ? null
                    : submissionBucket(state.players[playerId]);
        if (bucket === null)
            continue;
        const into = bucket === 'submissions' ? promoted : promotedSpectators;
        const next = promotedToBest(into[playerId], best, playerId, round.target);
        if (next !== null)
            into[playerId] = next;
    }
    return { submissions: promoted, spectatorSubmissions: promotedSpectators };
}
function judgeRound(state, now, judgments, spectatorJudgments, best) {
    if (!PLAY_PHASES.includes(state.phase))
        return fail('no round is in play', 'invalid-state');
    const round = currentRound(state);
    if (round === null)
        return fail('no round to judge', 'invalid-state');
    if (round.revealedAt !== null)
        return fail('this round is already judged', 'invalid-state');
    const draft = draftOf(state);
    const submissions = {};
    for (const [playerId, submission] of Object.entries(round.submissions)) {
        submissions[playerId] = judgedSubmission(submission, judgments[playerId]);
    }
    const spectatorSubmissions = {};
    for (const [playerId, submission] of Object.entries(round.spectatorSubmissions)) {
        spectatorSubmissions[playerId] = judgedSubmission(submission, spectatorJudgments[playerId]);
    }
    // The numbers round pays what the player REACHED. After judging rather than
    // before it, because a numbers judgment is built from the submission's own
    // value — promoting first would have the caller's copy of the old value
    // written straight back over the new one.
    const answered = withPromotedBests(state, round, submissions, spectatorSubmissions);
    let judged = patchRound(round, {
        submissions: answered.submissions,
        spectatorSubmissions: answered.spectatorSubmissions,
        best,
        revealedAt: now,
    });
    // **The audience seat, folded in here and nowhere else.** It is one answer
    // drawn from the bench's play-alongs, written into `submissions` under a
    // reserved id — so the reveal, the round score and the standing pick it up
    // through the code they already run, and nothing about it needed a branch in
    // `revealOrder`, `scoreRound` or `recomputeScores`.
    //
    // After the spectator answers are judged, because it can only choose among
    // answers that survived judging: a percentile over unjudged words would rank
    // an invented nine above a real five. Before the scoring pass below, because
    // it is scored by the same scorer as every other seat.
    //
    // It changes nothing about what a spectator can see. `spectatorSubmissions`
    // still has no field on `ClientRound` and still reaches only its own author;
    // what travels is one folded answer with nobody's name on it, at the reveal,
    // when every other answer travels. See `audienceSubmission`.
    const audience = audienceSubmission(judged, state.settings);
    if (audience !== null) {
        judged = patchRound(judged, {
            submissions: { ...judged.submissions, [AUDIENCE_SEAT_ID]: audience },
        });
    }
    // Scores need the judged round in place, so this is a second pass.
    const perPlayer = scoreRound(judged);
    const scored = {};
    for (const [playerId, submission] of Object.entries(judged.submissions)) {
        scored[playerId] = { ...submission, score: perPlayer[playerId] ?? 0 };
    }
    const scoredSpectators = {};
    for (const [playerId, submission] of Object.entries(judged.spectatorSubmissions)) {
        scoredSpectators[playerId] = { ...submission, score: scoreSpectator(judged, submission) };
    }
    // The reveal starts with nothing out and its first entry on the clock. The
    // `reveal-step` events follow one at a time from `tick`, at the moment each
    // answer actually becomes visible: sending them all here is what let the
    // client animate the whole round in a second and a half.
    judged = pacedReveal(patchRound(judged, {
        submissions: scored,
        spectatorSubmissions: scoredSpectators,
        revealCursor: 0,
    }), now, state.settings);
    draft.state = recomputeScores(replaceRound(draft.state, judged));
    setPhase(draft, revealPhaseFor(round.type));
    const lengths = {};
    for (const [playerId, submission] of Object.entries(scored)) {
        lengths[playerId] = submission.declaredLength;
    }
    draft.events.push({ type: 'declared-lengths', lengths });
    // A round with nothing to pace is finished the moment it is judged, and the
    // best answer goes out with the lengths rather than waiting on a queue that
    // will never move. Two of them: a round nobody answered, and every conundrum —
    // see `revealStepCount`.
    const paced = currentRound(draft.state);
    if (paced !== null && isRevealComplete(paced) && paced.best !== null) {
        draft.events.push({ type: 'reveal-best', best: paced.best });
    }
    draft.events.push({
        type: 'scores-updated',
        scores: standingScores(draft.state),
        teamScores: cumulativeTeamScores(draft.state),
    });
    pushLog(draft, 'round-ended', `round ${round.index + 1} was scored`, now);
    return done(draft);
}
/**
 * Every score the standing shows, by seat.
 *
 * Not `role === 'player'`: a player who stood down mid-game keeps the points
 * they had (see `leaveSeat`), and the audience seat is not a `ServerPlayer` at
 * all, so its running total comes out of `cumulativeTeamScores` — the one place
 * it is accumulated. One implementation, so the three events that carry a
 * standing cannot show three different ones.
 */
function standingScores(state) {
    const scores = {};
    for (const [playerId, player] of Object.entries(state.players)) {
        if (seatScoresThisGame(player))
            scores[playerId] = player.score;
    }
    if (state.settings.audienceSeat === true) {
        scores[AUDIENCE_SEAT_ID] = cumulativeTeamScores(state)[AUDIENCE_SEAT_ID] ?? 0;
    }
    return scores;
}
/**
 * The host's ruling on one answer, and the taking back of one.
 *
 * **Off unless the room turned it on.** `settings.hostRulings` gates the whole
 * feature and is false by default, because a room where the host can move the
 * scores is a room whose statistics are not worth keeping — and the fix for that
 * is not to COUNT it: see `gameCountsForStats`. The refusal is here rather than in
 * the socket layer so that nothing can reach the state by another door.
 *
 * `ruling: null` reverts. It restores `judgedValid` — what the dictionary said
 * before anybody argued with it — and the score comes back with it, because
 * `scoreRound` is a pure function of the round and is simply run again. That is
 * why there is nothing to remember beyond the one boolean, and why an undo of a
 * ruling that ALLOWED a word the dictionary had rejected puts the rejection back
 * rather than leaving the word standing.
 */
function overrideWord(state, now, playerId, targetId, ruling) {
    if (!isHost(state, playerId))
        return fail('only the host may override a word', 'not-host');
    if (state.settings.hostRulings !== true) {
        return fail('host rulings are turned off in this room', 'invalid-action');
    }
    const round = currentRound(state);
    if (round === null)
        return fail('no round to override', 'invalid-state');
    // A conundrum has exactly one right answer and the engine is holding it.
    // Overruling a letters ruling is a judgement call about a dictionary — the
    // host allowing a word the list has never heard of, or disallowing one it has
    // — and there is no equivalent judgement to make here: the scramble either
    // unpacks into a real word of the full length or it does not, and the engine
    // already checked. So the control is not "the host is usually right and
    // sometimes wrong", it is "the host may hand somebody ten points for a word
    // that is not there", which is not a ruling at all.
    if (round.type === 'conundrum') {
        return fail('a conundrum has one answer and it is not the host’s to change', 'invalid-action');
    }
    const submission = round.submissions[targetId] ?? round.spectatorSubmissions[targetId];
    if (submission === undefined)
        return fail('that player has no answer', 'invalid-action');
    if (submission.valid === null)
        return fail('that answer is not judged yet', 'invalid-state');
    const draft = draftOf(state);
    // Captured on the way past, for anything judged before `judgedValid` existed:
    // a room restored from an older snapshot has the field missing, and the moment
    // a ruling is applied is the last moment the original verdict is still in
    // `valid` where it can be copied.
    const judgedValid = submission.judgedValid ?? submission.valid;
    const overridden = {
        ...submission,
        judgedValid,
        hostOverride: ruling,
        valid: ruling === null ? judgedValid : ruling === 'allow',
    };
    const inMain = round.submissions[targetId] !== undefined;
    let next = patchRound(round, inMain
        ? { submissions: { ...round.submissions, [targetId]: overridden } }
        : { spectatorSubmissions: { ...round.spectatorSubmissions, [targetId]: overridden } });
    if (inMain) {
        const perPlayer = scoreRound(next);
        const rescored = {};
        for (const [id, entry] of Object.entries(next.submissions)) {
            rescored[id] = { ...entry, score: perPlayer[id] ?? 0 };
        }
        next = patchRound(next, { submissions: rescored });
    }
    else {
        const rescored = {
            ...next.spectatorSubmissions,
            [targetId]: { ...overridden, score: scoreSpectator(next, overridden) },
        };
        next = patchRound(next, { spectatorSubmissions: rescored });
    }
    draft.state = recomputeScores(replaceRound(draft.state, next));
    const by = draft.state.players[playerId]?.displayName ?? 'the host';
    draft.events.push({ type: 'word-override', playerId: targetId, ruling, by });
    draft.events.push({
        type: 'scores-updated',
        scores: standingScores(draft.state),
        teamScores: cumulativeTeamScores(draft.state),
    });
    const whose = draft.state.players[targetId]?.displayName ?? targetId;
    const what = ruling === null
        ? `took back their ruling on ${whose}'s answer`
        : `${ruling === 'allow' ? 'allowed' : 'rejected'} ${whose}'s answer`;
    pushLog(draft, 'word-override', `${by} ${what}`, now);
    return done(draft);
}
// ---------------------------------------------------------------------------
// Advancing
// ---------------------------------------------------------------------------
function enterGameOver(draft, now) {
    draft.state = {
        ...draft.state,
        endedAt: now,
        winnerIds: determineWinners(draft.state),
    };
    const round = currentRound(draft.state);
    if (round !== null && round.clock !== null) {
        draft.state = replaceRound(draft.state, mapRoundClocks(round, (clock) => pauseClock(clock, now)));
    }
    // A game ended in the middle of a reveal still owes everyone the rest of it.
    // The end screen shows the whole round, so the cursor goes to the end rather
    // than freezing three answers in.
    const closing = currentRound(draft.state);
    if (closing !== null && closing.revealedAt !== null && !isRevealComplete(closing)) {
        draft.state = replaceRound(draft.state, patchRound(closing, {
            revealCursor: revealStepCount(closing),
            nextRevealAt: null,
            revealAdvanceAt: null,
        }));
    }
    setPhase(draft, 'game-over');
    draft.events.push({
        type: 'game-over',
        winnerIds: draft.state.winnerIds,
        scores: standingScores(draft.state),
    });
    pushLog(draft, 'game-ended', 'the game ended', now);
}
/**
 * Shared by `advance` and by `tick`, so a missed tick and a manual advance agree.
 *
 * **A finished reveal goes straight to the next round.** There was a
 * `scoreboard` phase in between, eight seconds long, and it re-reported the
 * standing the reveal's closing slide had just performed — the same leaderboard
 * twice, the second time on a screen with no board and no running order to hang
 * it on. Removing it is why the reveal's button is named for what the press does
 * to the game rather than for the screen it opens.
 */
function advanceInternal(draft, now) {
    const phase = draft.state.phase;
    if (REVEAL_PHASES.includes(phase)) {
        const nextIndex = draft.state.currentRoundIndex + 1;
        if (nextIndex >= draft.state.settings.roundSequence.length) {
            enterGameOver(draft, now);
            return null;
        }
        enterRoundIntro(draft, nextIndex, now);
        return null;
    }
    return fail(`nothing to advance from ${phase}`, 'invalid-state');
}
function advance(state, now) {
    const draft = draftOf(state);
    const failure = advanceInternal(draft, now);
    return failure ?? done(draft);
}
/**
 * The host's hand on the reveal.
 *
 * Pressed while answers are still coming out it releases the rest of them at
 * once; pressed once they are all out it starts the next round, or ends the
 * game after the last one. One button, and it never has nothing to do, which is
 * why it does not simply fail when the reveal is unfinished — a host hurrying a
 * slow reveal along and a host done reading it are the same person pressing the
 * same thing.
 */
function advanceReveal(state, now, playerId) {
    if (!isHost(state, playerId))
        return fail('only the host may pace the reveal', 'not-host');
    if (!REVEAL_PHASES.includes(state.phase))
        return fail('no reveal is running', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.revealedAt === null) {
        return fail('no reveal is running', 'invalid-state');
    }
    const draft = draftOf(state);
    if (!isRevealComplete(round)) {
        releaseRemaining(draft, round, now);
        return done(draft);
    }
    const failure = advanceInternal(draft, now);
    return failure ?? done(draft);
}
/**
 * The host's hand on the reveal clock.
 *
 * **Built against the scoreboard, moved here 2026-08-01 when that phase went.**
 * It was the right control on the wrong screen. The room is not talking over a
 * standing it has already read — it is talking over the reveal, querying a word,
 * doing somebody's arithmetic out loud — and that is where the clock running out
 * mid-sentence actually costs something. It stops one clock and decides nothing,
 * which is what lets it exist at all on a screen whose rule is that no control
 * may change what happens next.
 *
 * **It holds the standing, never the queue, and it used to hold whichever wait
 * the reveal happened to be on.** `round.clock` is one clock — the gap before
 * the next answer while entries are coming out, and the auto-advance once they
 * are all out — and stopping it was the same act either way. It is not the same
 * act to a room. Reported by Isaac: pressing hold while the words were coming
 * out stopped them coming out, and because the queue is worst-first the answer
 * frozen off the screen is the best one in the round, every time. What a host
 * wants there is for the round to finish paying off and then wait.
 *
 * So a press before the queue is out arms `revealHoldAtEnd` and touches no
 * clock; `pacedReveal` pauses the auto-advance window at the moment it opens. A
 * press after the queue is out has nothing left to run and pauses that window
 * directly. Resuming clears both, which is why one action still does it.
 *
 * It reuses the ordinary pause arithmetic, so it pauses exactly the way a play
 * clock does — accumulated pause milliseconds against an absolute deadline,
 * never a remaining-time counter. That is what makes it survive a restart, and
 * it is why `clock.ts` needed no change.
 *
 * `syncRevealDeadlines` runs after it, which the scoreboard version did not need
 * and this one does: `nextRevealAt` and `revealAdvanceAt` are readable copies of
 * the clock's effective deadline, and a resume pushes that deadline out by
 * however long the hold lasted. Left alone they would say the next answer is
 * already overdue.
 *
 * Idempotent in both directions: pausing a paused clock and resuming a running
 * one are `pauseClock`/`resumeClock` no-ops, and a host whose button double-fires
 * must not resume-then-pause its way into a lost second.
 *
 * The client needs no new field to render this — `round.clock` travels whole, so
 * `clock.pausedAt !== null` is the held reveal.
 */
function setRevealPaused(state, now, playerId, paused) {
    if (!isHost(state, playerId))
        return fail('only the host may hold the reveal', 'not-host');
    if (!REVEAL_PHASES.includes(state.phase))
        return fail('no reveal is running', 'invalid-state');
    const round = currentRound(state);
    if (round === null || round.clock === null || round.revealedAt === null) {
        return fail('no reveal is running', 'invalid-state');
    }
    // **A hold pressed mid-reveal does not stop the reveal.** It arms
    // `revealHoldAtEnd`, and `pacedReveal` applies it when the queue runs out.
    // Isaac's report: holding to let the room talk stopped the answers coming
    // out, and the queue is worst-first, so what a hold cut off was always the
    // best word of the round. The full argument is on `RoundBase.revealHoldAtEnd`.
    //
    // The clock is left alone deliberately, which means the countdown to the next
    // answer keeps ticking and keeps being true. Nothing here pretends the reveal
    // has stopped when it has not.
    const armed = paused && !isRevealComplete(round);
    const clock = armed
        ? round.clock
        : paused
            ? pauseClock(round.clock, now)
            : resumeClock(round.clock, now);
    const holdAtEnd = paused;
    // Idempotent in both directions, and now against both fields: a host whose
    // button double-fires must not resume-then-pause its way into a lost second,
    // and must not disarm a hold by re-pressing it either.
    if (clock === round.clock && holdAtEnd === (round.revealHoldAtEnd === true)) {
        return done(draftOf(state));
    }
    const draft = draftOf(state);
    draft.state = replaceRound(draft.state, patchRound(round, { clock, revealHoldAtEnd: holdAtEnd }));
    draft.state = syncRevealDeadlines(draft.state);
    pushLog(draft, paused ? 'reveal-paused' : 'reveal-resumed', paused
        ? armed
            ? 'the host held the reveal at the standing'
            : 'the host held the reveal'
        : 'the host let the reveal carry on', now);
    return done(draft);
}
/** Push every remaining entry out at once, with the events that go with them. */
function releaseRemaining(draft, round, now) {
    const { round: next, released } = releaseReveal(round, now, revealStepCount(round), draft.state.settings);
    draft.state = replaceRound(draft.state, next);
    emitRevealSteps(draft, next, released);
}
/** One `reveal-step` per answer as it becomes visible, then the best answer. */
function emitRevealSteps(draft, round, released) {
    for (const playerId of released) {
        const submission = round.submissions[playerId];
        if (submission === undefined)
            continue;
        draft.events.push({
            type: 'reveal-step',
            playerId,
            submission: clientSubmissionOf(submission),
        });
    }
    if (isRevealComplete(round) && round.best !== null) {
        draft.events.push({ type: 'reveal-best', best: round.best });
    }
}
function forceSkipRound(state, now, playerId) {
    if (!isHost(state, playerId))
        return fail('only the host may skip a round', 'not-host');
    const inRound = PLAY_PHASES.includes(state.phase) ||
        SELECTION_PHASES.includes(state.phase) ||
        REVEAL_PHASES.includes(state.phase) ||
        state.phase === 'round-intro' ||
        // Named on its own rather than added to `SELECTION_PHASES`, which is also
        // read by `clockRunsFor` — the question there is whether a clock is running
        // against a player's ANSWER, and during the deal nobody is answering. The
        // host can still skip, because five seconds of board with a broken round
        // behind it is exactly when they would want to.
        state.phase === 'numbers-drawing';
    if (!inRound)
        return fail('no round to skip', 'invalid-state');
    const round = currentRound(state);
    if (round === null)
        return fail('no round to skip', 'invalid-state');
    const draft = draftOf(state);
    // A skipped round scores nothing: every submission stays unjudged, which
    // `scoreRound` already treats as zero. Its reveal is marked finished so the
    // screen and the end screen show the answers rather than an empty list waiting
    // on a cursor that will never move.
    //
    // It lands on the round's own reveal rather than on a phase of its own. That
    // used to be the scoreboard, and with the scoreboard gone the honest place for
    // "here is the round you just skipped" is the screen that shows a round: every
    // answer already out, the standing on the closing slide, and the ordinary
    // auto-advance underneath so an absent host cannot wedge it.
    const skipped = patchRound(round, {
        revealedAt: now,
        clock: startClock(REVEAL_AUTO_ADVANCE_MS, now),
        revealCursor: revealStepCount(round),
        nextRevealAt: null,
        revealAdvanceAt: now + REVEAL_AUTO_ADVANCE_MS,
    });
    draft.state = recomputeScores(replaceRound(draft.state, clearBuzz(skipped)));
    setPhase(draft, revealPhaseFor(round.type));
    pushLog(draft, 'round-ended', `round ${round.index + 1} was skipped by the host`, now);
    return done(draft);
}
function clearBuzz(round) {
    if (round.type !== 'conundrum')
        return round;
    return { ...round, buzzedBy: null, buzzClock: null };
}
function endGame(state, now, playerId) {
    if (playerId !== null && !isHost(state, playerId)) {
        return fail('only the host may end the game', 'not-host');
    }
    if (state.phase === 'lobby')
        return fail('no game is running', 'invalid-state');
    if (state.phase === 'game-over')
        return done(draftOf(state));
    const draft = draftOf(state);
    enterGameOver(draft, now);
    return done(draft);
}
function returnToLobby(state, now, playerId) {
    if (!isHost(state, playerId))
        return fail('only the host may return to the lobby', 'not-host');
    if (state.phase !== 'game-over')
        return fail('the game is not over', 'invalid-state');
    const draft = draftOf(state);
    const players = { ...draft.state.players };
    for (const id of Object.keys(players)) {
        players[id] = { ...players[id], score: 0, waitingForNextGame: false, leftSeatAt: null };
    }
    draft.state = {
        ...draft.state,
        players,
        phase: 'lobby',
        gameId: null,
        rounds: [],
        currentRoundIndex: 0,
        startedAt: null,
        endedAt: null,
        winnerIds: [],
        readyIds: [],
        // One extra step so the same player does not always pick first.
        chooserRotationIndex: draft.state.chooserRotationIndex + 1,
    };
    draft.events.push({ type: 'returned-to-lobby' });
    draft.events.push({ type: 'phase-changed', phase: 'lobby', roundIndex: 0 });
    pushLog(draft, 'game-ended', 'back to the lobby', now);
    return done(draft);
}
// ---------------------------------------------------------------------------
// tick
//
// Every transition that needs no data from the server happens here, so a
// missed `advance` can never wedge a room. Transitions that need drawn data or
// a dictionary are surfaced through `effectsFor` instead.
// ---------------------------------------------------------------------------
function tick(state, now) {
    const draft = draftOf(state);
    // Host transfer after verified downtime.
    const host = draft.state.players[draft.state.hostId];
    if (host !== undefined &&
        !host.isBot &&
        !host.connected &&
        host.disconnectedAt !== null &&
        now - host.disconnectedAt >= HOST_TRANSFER_AFTER_MS) {
        handoffHost(draft, now, true);
    }
    if (draft.state.serverPaused) {
        // The countdown elapsing is a state-only transition, so `tick` owns it the
        // way it owns every other one. Leaving it to the server would mean a room
        // whose countdown ran out while the wake-up was missed stays frozen for
        // good, with no second chance to notice.
        if (draft.state.resumingAt === null || now < draft.state.resumingAt)
            return done(draft);
        applyResume(draft, now);
    }
    const round = currentRound(draft.state);
    if (round === null)
        return done(draft);
    // An unanswered buzz is a wrong answer: lock them out, hand the clock back.
    if (round.type === 'conundrum' &&
        round.buzzedBy !== null &&
        round.buzzClock !== null &&
        isExpired(round.buzzClock, now)) {
        failBuzz(draft, round, round.buzzedBy, '', now);
        return done(draft);
    }
    const live = currentRound(draft.state);
    if (live === null || live.clock === null)
        return done(draft);
    if (REVEAL_PHASES.includes(draft.state.phase)) {
        if (!isExpired(live.clock, now))
            return done(draft);
        if (isRevealComplete(live)) {
            // Forty-five seconds with the last answer on screen and no host: move on
            // rather than let one absent player freeze the room.
            const failure = advanceInternal(draft, now);
            if (failure !== null)
                return failure;
            return done(draft);
        }
        // Exactly one step per tick, and the next wait is measured from `now`
        // rather than from the deadline that just passed, so a late wake-up costs a
        // beat rather than firing the rest of the reveal in one frame. The last step
        // releases nobody: it is the hold on the final answer, and consuming it is
        // what completes the reveal.
        const { round: next, released } = releaseReveal(live, now, 1, draft.state.settings);
        draft.state = replaceRound(draft.state, next);
        emitRevealSteps(draft, next, released);
        return done(draft);
    }
    // Freeze an expired play or selection clock exactly at its deadline. That is
    // both the honest record and the only state-only signal that time is up, which
    // `toClientState` needs since it has no `now`.
    const frozen = freezeAtDeadline(live.clock, now);
    if (frozen !== live.clock) {
        draft.state = replaceRound(draft.state, patchRound(live, { clock: frozen }));
    }
    return done(draft);
}
// ---------------------------------------------------------------------------
// Server lifecycle
// ---------------------------------------------------------------------------
function serverPause(state, now, reason) {
    const draft = draftOf(state);
    draft.state = {
        ...draft.state,
        serverPaused: true,
        serverPauseReason: reason,
        // A second pause landing mid-countdown cancels it. Carried through a
        // shutdown instead, it restores as a deadline already in the past, which
        // `resumeIfWatched` reads as a countdown already running and declines to
        // start — and the room is frozen with nothing left to unfreeze it.
        resumingAt: null,
        // A server pause stops EVERY clock, because the server decided. That is
        // fair, and it is what makes a redeploy mid-round cost nobody any time.
        rounds: draft.state.rounds.map((round) => mapRoundClocks(round, (clock) => isStoppedAtDeadline(clock) ? clock : pauseClock(clock, now))),
    };
    draft.events.push({ type: 'server-paused', reason });
    pushLog(draft, 'server-paused', `paused: ${reason}`, now);
    return done(draft);
}
function beginResumeCountdown(state, now) {
    if (!state.serverPaused)
        return fail('the server is not paused', 'invalid-state');
    // A countdown already running is left alone. Every player from a restored room
    // comes back at once, and restarting the three seconds on each arrival would
    // let a steady trickle of reconnects postpone the resume indefinitely.
    if (state.resumingAt !== null)
        return { ok: true, state, events: [] };
    const draft = draftOf(state);
    const resumeAt = now + RESUME_COUNTDOWN_MS;
    draft.state = { ...draft.state, resumingAt: resumeAt };
    draft.events.push({ type: 'server-resuming', resumeAt });
    return done(draft);
}
/**
 * Re-derive the reveal's readable deadlines from its clock.
 *
 * Called wherever a clock is resumed. `resumeClock` pushes the effective
 * deadline out by however long the pause lasted, and `nextRevealAt` and
 * `revealAdvanceAt` are copies of it — left alone, a room that came back from a
 * restart ten minutes later would fire its whole reveal in one frame.
 */
function syncRevealDeadlines(state) {
    if (!REVEAL_PHASES.includes(state.phase))
        return state;
    const round = currentRound(state);
    if (round === null || round.revealedAt === null || round.clock === null)
        return state;
    const at = effectiveDeadline(round.clock);
    const complete = isRevealComplete(round);
    return replaceRound(state, patchRound(round, {
        nextRevealAt: complete ? null : at,
        revealAdvanceAt: complete ? at : null,
    }));
}
/** Lift the pause on a draft, so `tick` can do it without discarding its events. */
function applyResume(draft, now) {
    draft.state = {
        ...draft.state,
        serverPaused: false,
        serverPauseReason: null,
        resumingAt: null,
        rounds: draft.state.rounds.map((round) => mapRoundClocks(round, (clock) => 
        // A clock stopped at its own deadline stays there. Resuming it would
        // hand the room a fresh round.
        isStoppedAtDeadline(clock) ? clock : resumeClock(clock, now))),
    };
    draft.state = syncRevealDeadlines(draft.state);
    draft.events.push({ type: 'server-resumed' });
    pushLog(draft, 'server-resumed', 'resumed', now);
}
function serverResume(state, now) {
    if (!state.serverPaused)
        return fail('the server is not paused', 'invalid-state');
    const draft = draftOf(state);
    applyResume(draft, now);
    return done(draft);
}
// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------
function chat(state, now, action) {
    const player = state.players[action.playerId];
    if (player === undefined)
        return fail('no such player', 'invalid-action');
    if (player.muted)
        return fail('you are muted', 'invalid-action');
    if (state.chat.some((message) => message.id === action.messageId)) {
        return { ok: true, state, events: [] };
    }
    // Who may speak. A safety control rather than a rule of the game, which is why
    // it is on `SAFETY_SETTINGS` and can be reached mid-round: the moment a host
    // needs it is the moment something is going wrong. `off` takes the panel off
    // the screen entirely, so a client that keeps sending is a client that has not
    // caught up, and refusing it here is what makes the setting true rather than
    // cosmetic. System lines do not come through this function at all.
    const mode = state.settings.chatMode ?? 'everyone';
    if (mode === 'off')
        return fail('chat is switched off in this room', 'invalid-action');
    if (mode === 'players' && player.role === 'spectator') {
        return fail('only players may talk in this room', 'invalid-action');
    }
    const checked = validateChatBody(action.body);
    if (!checked.ok)
        return fail(checked.error, 'invalid-action');
    if (action.channel === 'team' && (player.teamId === null || !teamsAreOn(state))) {
        return fail('you have no team to talk to', 'invalid-action');
    }
    const draft = draftOf(state);
    pushChat(draft, {
        id: action.messageId,
        channel: action.channel,
        authorId: player.id,
        authorName: player.displayName,
        authorUsername: player.username,
        teamId: action.channel === 'team' ? player.teamId : null,
        fromSpectator: player.role === 'spectator',
        body: checked.value,
        censored: action.censored,
        at: now,
        system: false,
    });
    return done(draft);
}
/**
 * The host takes one line down, and it goes.
 *
 * **A removal rather than a tombstone, reversed on Isaac's instruction on
 * 2026-08-04.** The row used to stay with its body emptied, on two arguments:
 * that a line which silently vanishes turns every reply to it into a
 * non-sequitur, and that `RoomShell` counted unread as `chat.length - seen`, so
 * a shrinking array put the count behind. The second was real and is answered
 * structurally — `chatSeq` only goes up — and the first lost to `purge-chat`,
 * where taking forty lines off one author leaves forty markers and the moderated
 * room reads worse than the unmoderated one.
 *
 * **The gate is here rather than in `socket.ts`.** Mute and transfer are checked
 * up there, on `HOST_ONLY_MESSAGES`, and this one is checked here for the same
 * reason `override-word`, `force-skip-round` and `end-game` are: who may delete
 * a message is a rule of the room, so it belongs where the rules are testable
 * without a socket. Nothing stops the socket layer checking too — it is a second
 * lock on the same door — but this is the one that must not be removed.
 *
 * **Three refusals, and two of them say the same thing on purpose.** An id that
 * names nothing and an id naming a team line this host was never shown are one
 * answer, because telling them apart is a way to ask what another team said; see
 * `canSeeChatMessage`. A system line is refused separately and can be, because a
 * system line is on everybody's screen already and there is nothing to learn
 * from being told it exists — it is the room's own record of what happened to
 * it, and a host who could edit that could kick somebody and then delete the
 * fact.
 *
 * **A repeat is now refused rather than answered `ok`, and that follows from the
 * removal rather than being a second decision.** With a tombstone the engine
 * could see that the line had been deleted and say so; with the line gone there
 * is nothing left to tell "already deleted" from "never existed", and the
 * refusal-symmetry rule above fixes what an id naming nothing gets. A client
 * retrying after a reconnect therefore sees a refusal for work that was done —
 * which is why the action still carries no `actionId`: there is no state for a
 * replay guard to protect, and composing a server-side id here would put the
 * server on the hook for the room-lifetime uniqueness rule in CLAUDE.md for
 * nothing.
 */
function deleteChatMessage(state, now, playerId, messageId) {
    const host = state.players[playerId];
    if (host === undefined)
        return fail('no such player', 'invalid-action');
    if (!isHost(state, playerId))
        return fail('only the host may delete a message', 'not-host');
    const message = state.chat.find((entry) => entry.id === messageId);
    if (message === undefined || !canSeeChatMessage(message, host)) {
        return fail('no such message', 'invalid-action');
    }
    if (message.system)
        return fail('that line is the room’s own record', 'invalid-action');
    const draft = draftOf(state);
    draft.state = {
        ...draft.state,
        chat: draft.state.chat.filter((entry) => entry.id !== messageId),
    };
    // **The only moderation record now**, where it used to be the second of two.
    // It names the host and the author, and it outlives `CHAT_HISTORY_LIMIT`
    // scrolling the conversation away — which the tombstone never did.
    pushLog(draft, 'chat-deleted', `${host.displayName} deleted a message from ${message.authorName ?? 'someone'}`, now);
    return done(draft);
}
/**
 * Take down everything one author has said in this room.
 *
 * Automatic on a ban, and available on its own for the case a host kicks
 * somebody first and thinks better of what they left behind afterwards. It is
 * keyed on the id the MESSAGES carry rather than on a seat, because `authorId`
 * naming somebody who has already gone is the main case rather than an edge one.
 *
 * **Room-scoped, always.** It filters `state.chat`, which is this room's and
 * nothing else's; there is no shape of this that reaches another room.
 *
 * **System lines are never touched**, on exactly the reasoning that refuses one
 * to `delete-chat-message`: they are the room's own record, they carry no
 * author, and a purge that swept them would let a host ban somebody and then
 * remove the line saying so.
 *
 * Team lines this host was never shown DO go. That is not a hole in the
 * refusal-symmetry rule but the other side of it: this action names an author,
 * never a message, so it answers the same way — nothing at all — whether the
 * author said one thing, forty things, or nothing. There is no id to probe with
 * and no count comes back.
 */
function purgeChat(state, now, playerId, authorId) {
    const host = state.players[playerId];
    if (host === undefined)
        return fail('no such player', 'invalid-action');
    if (!isHost(state, playerId))
        return fail('only the host may purge the chat', 'not-host');
    const doomed = state.chat.filter((entry) => !entry.system && entry.authorId === authorId);
    // Nothing to take down is success with nothing done, and deliberately looks
    // exactly like an author who never said anything. A log line here would be the
    // count this refuses to return, written somewhere else.
    if (doomed.length === 0)
        return { ok: true, state, events: [] };
    const draft = draftOf(state);
    draft.state = {
        ...draft.state,
        chat: draft.state.chat.filter((entry) => entry.system || entry.authorId !== authorId),
    };
    const whose = doomed.find((entry) => entry.authorName !== null)?.authorName ??
        state.players[authorId]?.displayName ??
        'someone';
    pushLog(draft, 'chat-deleted', `${host.displayName} deleted every message from ${whose}`, now);
    return done(draft);
}
function systemMessage(state, now, body) {
    const draft = draftOf(state);
    pushChat(draft, {
        id: `sys-${now}-${state.chat.length}`,
        channel: 'global',
        authorId: null,
        authorName: null,
        authorUsername: null,
        teamId: null,
        fromSpectator: false,
        body,
        censored: false,
        at: now,
        system: true,
    });
    return done(draft);
}
// ---------------------------------------------------------------------------
// effectsFor
// ---------------------------------------------------------------------------
/**
 * Every seated player who is actually there has locked, so the round is over.
 *
 * A clock running with nothing left to decide is nonsense, so the round ends the
 * moment the last answer is in rather than sitting out the remaining twenty
 * seconds. Three details carry the rule:
 *
 *   - Only connected players count. A player whose wifi died must not be able to
 *     hold the round open for everyone else by never locking, and their draft is
 *     adopted at judging anyway, so they lose nothing by it.
 *   - A lock is a ready signal, so unlocking or simply typing again takes a
 *     player back out of the count and the round carries on. That is the whole
 *     interaction: this is "everybody says they are done", not "everybody has
 *     spent their answer".
 *   - The conundrum is excluded. Nobody locks there; it ends when it is solved,
 *     when everybody is locked out, or when the clock runs down.
 */
function everyoneLocked(round, state) {
    if (round.type === 'conundrum')
        return false;
    const waitingOn = seatedIds(state).filter((id) => state.players[id]?.connected === true);
    if (waitingOn.length === 0)
        return false;
    return waitingOn.every((id) => round.submissions[id]?.lockedAt != null);
}
/** What the server must do next, derived from state alone. */
export function effectsFor(state, now) {
    const round = currentRound(state);
    let needsJudging = false;
    let needsNextRound = false;
    const candidates = [];
    const consider = (at) => {
        if (typeof at === 'number' && Number.isFinite(at))
            candidates.push(at);
    };
    // A pending host transfer, which `tick` resolves — but only when there is
    // somebody to hand the room to. Offered unconditionally, an absent host with
    // no successor leaves a deadline that `tick` can never clear, so it sits in
    // the past for ever and the scheduler's floor turns it into a 50 Hz loop.
    const host = state.players[state.hostId];
    if (host !== undefined &&
        !host.isBot &&
        !host.connected &&
        host.disconnectedAt !== null &&
        hostSuccessor(state) !== null) {
        consider(host.disconnectedAt + HOST_TRANSFER_AFTER_MS);
    }
    // Past or not: `tick` performs the resume, so a wake-up that arrived late
    // still has work to do, and skipping it would strand the room paused.
    if (state.resumingAt !== null)
        consider(state.resumingAt);
    if (state.serverPaused || round === null) {
        return { needsJudging, needsNextRound, nextTickAt: earliest(candidates) };
    }
    const clock = round.clock;
    const phase = state.phase;
    if (phase === 'round-intro') {
        // The server draws the piles or the conundrum and sends `begin-round`.
        if (clock !== null) {
            if (isExpired(clock, now))
                needsNextRound = true;
            else
                consider(effectiveDeadline(clock));
        }
    }
    else if (phase === 'letters-selection') {
        if (clock !== null) {
            if (isExpired(clock, now)) {
                // The server owns the random source, so it fires `auto-fill-letters`.
                needsNextRound = round.type === 'letters' && round.tiles.length < LETTERS_TILE_COUNT;
            }
            else {
                consider(effectiveDeadline(clock));
            }
        }
    }
    else if (phase === 'numbers-selection') {
        if (clock !== null) {
            if (isExpired(clock, now)) {
                // Nobody called it, so the server calls the default one large.
                needsNextRound = round.type === 'numbers' && round.largeCount === null;
            }
            else {
                consider(effectiveDeadline(clock));
            }
        }
    }
    else if (phase === 'numbers-drawing') {
        // The deal, one wake-up per tile and one more for the target. The server
        // owns the random source, so every one of them comes back through
        // `supplyRoundData`.
        //
        // The pause guard is the CLAUDE.md rule and not defensive noise: a paused
        // clock's deadline does not move, so offering it as the next wake-up hands
        // the scheduler a time in the past, `tick` declines, and `effectsFor`
        // offers the same one again — a busy loop for as long as the pause lasts.
        //
        // **But `pausedAt !== null` is not the same question as "is this paused",
        // and reading it as one wedges the deal on the first tile.** `tick` freezes
        // every expired play or selection clock AT its deadline, which is a stop
        // rather than a pause — the honest record that time is up, and the only
        // state-only signal `toClientState` has. Every deal clock therefore arrives
        // here with `pausedAt` set the moment it expires, which is precisely when
        // the next number is due. `isStoppedAtDeadline` is the existing distinction
        // and `serverPause` already leans on it for the same reason.
        if (clock !== null && (clock.pausedAt === null || isStoppedAtDeadline(clock))) {
            if (isExpired(clock, now))
                needsNextRound = true;
            else
                consider(effectiveDeadline(clock));
        }
    }
    else if (PLAY_PHASES.includes(phase)) {
        const solved = round.type === 'conundrum' && round.solvedBy !== null;
        const exhausted = round.type === 'conundrum' && conundrumExhausted(round, state);
        const timeUp = clock !== null && isExpired(clock, now);
        if (round.revealedAt === null && (solved || exhausted || timeUp || everyoneLocked(round, state))) {
            needsJudging = true;
        }
        else if (clock !== null && clock.pausedAt === null) {
            consider(effectiveDeadline(clock));
        }
        if (round.type === 'conundrum' && round.buzzClock !== null && round.buzzedBy !== null) {
            // Expired or not: `tick` turns an unanswered buzz into a wrong answer.
            consider(effectiveDeadline(round.buzzClock));
        }
    }
    else if (REVEAL_PHASES.includes(phase)) {
        // `tick` performs these, so a deadline in the past is still worth waking for
        // — but only while the clock is running. A paused clock's `deadline` does
        // not move (the pause is banked into `accumulatedPauseMs` on resume, not
        // before), so offering it here hands the scheduler a wake-up in the past
        // that `tick` correctly refuses to act on, and it re-offers the same one
        // immediately: the host holding the reveal would spin the room at the
        // scheduler's floor for as long as they held it. The play branch above has
        // guarded this since it learned to pause; this branch never had to until
        // the host gained a pause, and the pause now lives here rather than on the
        // scoreboard that used to own it — so this guard covers every wait the
        // reveal has, the gaps between answers included. Nothing is lost by staying
        // asleep, because the resume is itself an action and recomputes this.
        if (clock !== null && clock.pausedAt === null)
            consider(effectiveDeadline(clock));
    }
    return { needsJudging, needsNextRound, nextTickAt: earliest(candidates) };
}
function earliest(candidates) {
    if (candidates.length === 0)
        return null;
    let best = candidates[0];
    for (const candidate of candidates) {
        if (candidate < best)
            best = candidate;
    }
    return best;
}
// ---------------------------------------------------------------------------
// toClientState
//
// The one door between hidden state and a client. Everything below is either a
// deliberate copy or a deliberate omission.
// ---------------------------------------------------------------------------
/**
 * Whether declared lengths are public for this round yet.
 *
 * During the round other players show a lock indicator and nothing else. The
 * lengths go public the moment the clock stops at its deadline, which is what
 * `tick` records, and stay public through the reveal.
 */
function declaredLengthsVisible(round) {
    if (round.revealedAt !== null)
        return true;
    return round.clock !== null && isStoppedAtDeadline(round.clock);
}
function hiddenSubmissionOf(submission, showDeclared) {
    return {
        playerId: submission.playerId,
        // Derived, not asserted. Having a submission stored is not the same as
        // being ready: under open answering a player has one from their first
        // offered word, and hardcoding this meant the whole room lit up as locked
        // within seconds and the indicator stopped meaning anything. Same rule as
        // the player's own view.
        locked: submission.lockedAt !== null || submission.fromDraft,
        declaredLength: showDeclared ? submission.declaredLength : null,
        valid: null,
        score: null,
        // Null with `valid`, and for the same reason: before the reveal, whether the
        // dictionary took somebody's word is exactly the fact being withheld.
        judgedValid: null,
        hostOverride: submission.hostOverride,
    };
}
function toClientRound(round, playerId, adminView, aggregateOver) {
    const revealed = round.revealedAt !== null || adminView;
    const showDeclared = declaredLengthsVisible(round) || adminView;
    const submissions = {};
    for (const [id, submission] of Object.entries(round.submissions)) {
        if (revealed || id === playerId) {
            submissions[id] = clientSubmissionOf(submission);
            continue;
        }
        // Somebody else's answer, mid-round. Under open answering a player has a
        // stored submission from the first valid word they offer, long before they
        // are ready — so the *existence* of an entry became a signal in itself:
        // "this one has found something". The room is entitled to two facts during
        // a round, who has locked and, once the clock stops, how long everyone's
        // answer is. Anything else is a read on how the opposition is doing.
        const locked = submission.lockedAt !== null || submission.fromDraft;
        if (!locked && !showDeclared)
            continue;
        submissions[id] = hiddenSubmissionOf(submission, showDeclared);
    }
    // A spectator's play-along answer is only ever their own business. It reaches
    // them through `ownSubmission`, and `ClientRound` has nowhere else to put it,
    // so there is no filter to forget.
    const ownSubmission = round.submissions[playerId] ?? round.spectatorSubmissions[playerId] ?? null;
    // The order is a fact about answers that are already public, so it travels
    // only once the round is judged. Sent during play it would rank everyone by
    // the declared lengths the round is deliberately hiding.
    const order = round.revealedAt === null ? [] : revealOrder(round);
    const complete = isRevealComplete(round) || adminView;
    const base = {
        index: round.index,
        type: round.type,
        chooserId: round.chooserId,
        clock: round.clock,
        startedAt: round.startedAt,
        revealedAt: round.revealedAt,
        submissions,
        ownSubmission,
        // The best possible answer lands with the last entry, not with the first.
        // It is the punchline of the reveal and it gives away everyone's score.
        best: complete ? round.best : null,
        revealOrder: order,
        // Null on every round of an ordinary game, so a client that never renders it
        // costs nothing — and null before judging in every game, because it is built
        // from the judged submissions and must reveal nothing a beat earlier than
        // `best` and `revealOrder` do. `buildRoundAggregate` makes that second gate
        // itself rather than trusting the caller with it.
        aggregate: aggregateOver === null ? null : buildRoundAggregate(round, aggregateOver),
        revealCursor: revealCursorOf(round),
        revealComplete: complete,
        revealAdvanceAt: round.revealAdvanceAt ?? null,
        // Not derivable from `clock.pausedAt`: an armed hold leaves the clock
        // running on purpose, so the control that toggles it has to be told.
        revealHoldAtEnd: round.revealHoldAtEnd === true,
    };
    switch (round.type) {
        case 'letters':
            // `tiles` only ever holds letters already revealed to the room. The piles
            // have no field on ClientRound, so they cannot travel.
            base.tiles = round.tiles;
            break;
        case 'numbers':
            // Both fields are sent whole and neither needs a gate, which is the point
            // of dealing the board through actions rather than drawing it up front:
            // `tiles` holds only what has actually been revealed and `target` is null
            // until the moment the room is meant to have it. There is nothing here to
            // withhold because nothing unrevealed was ever put in the state. Draw all
            // seven at `choose-large-count` and slice them here instead, and this
            // line becomes the only thing standing between a curious player and the
            // whole round.
            base.numberTiles = round.tiles;
            base.largeCount = round.largeCount;
            base.target = round.target;
            // One player's best, to that player, for the same reason a spectator's
            // play-along only ever reaches its owner: `ClientRound` has one slot and
            // the server has a map, so there is no filter here to get wrong. Sent as
            // a map it would be a live read on how well everyone else is doing — worth
            // strictly more than the declared lengths this round already withholds
            // until the clock stops. Keyed lookup, never a spread of `round.bests`.
            base.ownNumbersBest = round.bests?.[playerId] ?? null;
            break;
        case 'conundrum':
            // No `solution` field exists on ClientRound. Structural, not filtered.
            base.scramble = round.scramble;
            base.buzzedBy = round.buzzedBy;
            base.buzzClock = round.buzzClock;
            base.lockedOut = round.lockedOut;
            base.solvedBy = round.solvedBy;
            // **Gated on the round being judged, beside the gate on `submissions`.**
            // These are answers to the scramble on the screen. Sent a tick early they
            // are the round handed to everybody in it, and the room's own AI seats
            // would be solving it for the humans.
            //
            // `revealedAt`, not `complete`: the whole conundrum is available the
            // moment it is judged — see `buildSlides` — and holding these behind the
            // cursor would hold them behind a queue a conundrum does not have.
            base.shadowAnswers =
                round.revealedAt === null ? [] : Object.values(round.shadowAnswers ?? {});
            break;
    }
    if (adminView) {
        // The deliberate admin spy view: everything, piles and solution included.
        return { ...round, ...base };
    }
    return base;
}
/**
 * Who counts as ready.
 *
 * Every seated bot is, always. A bot has nothing to wait for and no way to press
 * anything, so a room holding one would otherwise read "3 of 4 ready" for ever
 * and the host's "everyone is ready" moment would never arrive. They are added
 * here rather than written into `readyIds` so that nothing has to remember to
 * take them out again when they are removed or reseated.
 */
/**
 * The id of the record this game has written, or null when there is not one.
 *
 * Narrower than `state.gameId` on purpose, and every clause is a case where the
 * page it would link to does not exist:
 *
 *   - the game is still being played. The record is written on the transition
 *     into `game-over`, so before that there is an id and no page.
 *   - `buildGameRecord` would return null: no id, never started, or no seats
 *     left in it. The seat predicate is the same one it uses, `playerOrder`
 *     filtered on role, so the two cannot drift into disagreeing.
 *
 * A link to a 404 is worse than no link, which is why this is derived rather
 * than the raw field being handed over.
 */
function recordedGameIdOf(state) {
    if (state.gameId === null || state.startedAt === null)
        return null;
    if (state.phase !== 'game-over')
        return null;
    // Host rulings NO LONGER take the page away. They used to: the whole
    // `recordGame` write was skipped, so this returned null and a room that had
    // the setting on had nothing to link to for any game it ever played. The game
    // is written and marked `ranked: 0` now — see `gameCountsForStats` — so there
    // is a page, and it is the aggregates that leave it out.
    if (seatedIds(state).length === 0)
        return null;
    return state.gameId;
}
function readyIdsFor(state) {
    const ready = new Set(state.readyIds ?? []);
    for (const id of seatedIds(state)) {
        if (state.players[id]?.isBot === true)
            ready.add(id);
    }
    return [...ready];
}
/**
 * Per-player sanitization. Strips other players' locked answers before the
 * reveal, their declared lengths during the round, the best possible answer
 * before the reveal, the conundrum solution, the letter piles, every
 * server-only identity field, and other players' spectator play-alongs. Drafts
 * never enter ServerGameState at all, so they cannot leak here.
 *
 * It also withholds the moderation log from everyone but an admin viewer. That
 * one is about weight rather than secrecy: see the note on `log` below.
 */
export function toClientState(state, playerId, options) {
    const adminView = options?.adminView === true;
    const you = state.players[playerId];
    const ordered = [
        ...state.playerOrder.filter((id) => state.players[id] !== undefined),
        ...Object.keys(state.players)
            .filter((id) => !state.playerOrder.includes(id))
            .sort((a, b) => {
            const left = state.players[a];
            const right = state.players[b];
            if (left.joinedAt !== right.joinedAt)
                return left.joinedAt - right.joinedAt;
            return left.id < right.id ? -1 : 1;
        }),
    ];
    const players = ordered.map((id) => {
        const player = state.players[id];
        const publicView = publicPlayerOf(state, player);
        // reconnectToken, ip, deviceId and userId have no home on PublicPlayer.
        return adminView ? { ...player, ...publicView } : publicView;
    });
    // The audience seat, drawn here and stored nowhere. It has to be in `players`
    // for the standing and the reveal to have a name and a colour to put beside
    // its answer, and it must NOT be in `state.players` for the reasons on
    // `AUDIENCE_SEAT_ID` — chiefly that `playerOrder` is the chooser rotation, and
    // a seat nobody sits in cannot be asked to call a board.
    //
    // It is appended to `playerOrder` for the same reason and no other: that array
    // is what the running order and the reveal iterate. Nothing on the server side
    // reads this copy.
    // Not `settings.audienceSeat` — see `audienceSeatAppears`, which is what stops
    // a room drawing a competitor nobody is behind.
    const audienceSeatOn = audienceSeatAppears(state);
    // Computed once and used twice. It walks every round for every seat, so in a
    // five-hundred-seat room asking it a second time per broadcast per player is
    // not free.
    const teamScores = cumulativeTeamScores(state);
    const audienceScore = audienceSeatOn ? (teamScores[AUDIENCE_SEAT_ID] ?? 0) : 0;
    if (audienceSeatOn) {
        players.push({
            id: AUDIENCE_SEAT_ID,
            username: null,
            registered: false,
            displayName: 'The audience',
            // No colour, at any room size. It is not a person and a palette entry
            // would take one away from somebody who is.
            color: null,
            isBot: false,
            botKind: null,
            botDifficulty: null,
            botModel: null,
            botReasoning: false,
            role: 'player',
            leftSeat: false,
            teamId: null,
            score: audienceScore,
            connected: true,
            muted: false,
            waitingForNextGame: false,
            isHost: false,
        });
    }
    // Team chat is scoped to the team. A spectator's line is visible to everyone
    // and greyed by the client.
    //
    // The rule itself is `canSeeChatMessage`, shared with the host's delete so the
    // two cannot drift; the admin view is the one thing that is not a rule about
    // being in the conversation, so it stays here.
    const chatVisible = state.chat.filter((message) => adminView || canSeeChatMessage(message, you));
    // Whether this game's rounds are drawn as a distribution, and the seats it is
    // counted over. One question asked once per broadcast rather than once per
    // round: `revealIsAggregated` is a property of the room, so a game whose seat
    // count changed mid-round would otherwise aggregate some of its rounds and not
    // others. The audience is one of the seats it counts, because it answers like
    // one and a `noAnswer` that ignored it would be off by one every round.
    const seats = seatedIds(state);
    const aggregateSeats = audienceSeatOn ? [...seats, AUDIENCE_SEAT_ID] : seats;
    const aggregateOver = revealIsAggregated(state.settings, aggregateSeats.length)
        ? aggregateSeats
        : null;
    return {
        roomCode: state.roomCode,
        gameId: recordedGameIdOf(state),
        // Raw, and deliberately not run through the narrowing above: this is the
        // question "what game is this room playing", which has an answer during the
        // game and is the only moment it is useful. See `ClientGameState.liveGameId`
        // for why the two are separate fields rather than one widened one.
        liveGameId: state.gameId,
        phase: state.phase,
        settings: state.settings,
        players,
        playerOrder: audienceSeatOn ? [...state.playerOrder, AUDIENCE_SEAT_ID] : state.playerOrder,
        teams: state.teams ?? [],
        hostId: state.hostId,
        // Everyone's readiness, not just the viewer's. Readying up is a message to
        // the room; a lobby where each client could only see its own flag reported
        // "1 of 4 ready" to four different people at once.
        readyIds: readyIdsFor(state),
        currentRoundIndex: state.currentRoundIndex,
        rounds: state.rounds.map((round) => toClientRound(round, playerId, adminView, aggregateOver)),
        totalRounds: state.settings.roundSequence.length,
        serverPaused: state.serverPaused,
        serverPauseReason: state.serverPauseReason,
        resumingAt: state.resumingAt,
        startedAt: state.startedAt,
        endedAt: state.endedAt,
        winnerIds: state.winnerIds,
        winners: resolveWinners(state),
        youId: playerId,
        youAreHost: state.hostId === playerId,
        // The seat's own answer to "am I signed in", which is the only answer that
        // decides anything. What the browser tab believes about Clerk is a different
        // question and was being asked in place of this one, so the lobby lit the
        // Add AI button for a player the socket layer then refused.
        youAreSignedIn: (you?.userId ?? null) !== null,
        youAreAdmin: adminView,
        // One rule, read here and by `persistGame`, so the room's banner and the
        // row on the disk cannot disagree. See `gameCountsForStats`. It no longer
        // means "there will be no page" — there is one — it means the game is not
        // counted anywhere.
        unranked: !gameCountsForStats(state.settings),
        // Absolute, never a remaining-ms counter, and null whenever the idle sweep
        // would refuse to act. A caller that supplies nothing gets null, which is
        // the honest answer for a state nobody is holding a Room around.
        roomExpiresAt: options?.idleDeadlineAt ?? null,
        chat: chatVisible,
        // Monotonic, and the thing an unread count is measured against. It counts
        // every line the ROOM has appended, not the ones this viewer may read: a
        // per-viewer count would go down for a host who left a team, which is the
        // bug this exists to make impossible. See `ServerGameState.chatSeq`.
        chatSeq: state.chatSeq ?? 0,
        // The moderation log is for the admin spy view, and only ever went to
        // everybody because it was cheaper to write that way. Up to
        // LOG_HISTORY_LIMIT entries rode along on every broadcast to every player,
        // and no ordinary screen has ever rendered one. The entries themselves are
        // kept in full on the server: this is a delivery decision, not a retention
        // one. Ordinary players still get each entry as it happens, through the
        // `log-entry` event, which is what a moderation view attached mid-game
        // would build from anyway.
        log: adminView ? state.log : [],
        teamScores,
    };
}
